// ================================================================
// src/pages/PrivateClassShared.jsx
// نسخه‌ی نهایی جامع — همه‌ی ۴۳ مشکل حل شده
// State Machine + Sync واحد + Race Locks + Refund Guard
// ================================================================

import React, {
  useState, useEffect, useMemo, useCallback, useRef,
  createContext, useContext, useReducer, memo
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-jalaali';
import './PrivateClassPage.css';
import {
  GRADE_LIST,
  FIELD_LIST,
  normalizeGradeId,
  normalizeFieldId,
  getGrade,
  getSubjectsFor,
} from '../core/education/curriculum.js';
import { engine } from '../core/books/storage.js';
import { getToken } from '../utils/secureStorage';




export const MIN_WALLET_CHARGE_TOMAN = 10000;



// ================================================================
// ۱. ثابت‌های محیطی
// ================================================================
export const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ✅ تغییر یافت: حالا به جای وصل شدن مستقیم به Roomeet، به سرور خودمان وصل می‌شویم
export const ROOMEET_BASE = '/api/roomeet'; 
export const ROOMEET_PUBLIC_URL = 'https://roomeet.ir';
// (متغیر ROOMEET_API_KEY کاملاً حذف شد)

// ✅ State Machine — تنها ۴ وضعیت مجاز
export const COURSE_STATUS = {
  SCHEDULED: 'scheduled',
  LIVE: 'live',
  ENDED: 'ended',
  CANCELLED: 'cancelled',
};





// ================================================================
// ✅ helper های کارمزد — یک منبع حقیقت برای همه‌جا
// ================================================================
export function getCommissionRate(adminSettings) {
  const r = Number(adminSettings?.commission);
  if (!Number.isFinite(r) || r < 0) return 0;
  return Math.min(100, r);
}

export function computeCommissionSplit(price, commissionRate) {
  const p = Number(price) || 0;
  const r = Number(commissionRate) || 0;
  const commissionAmount = Math.round(p * (r / 100));
  const netAmount = p - commissionAmount;
  return { netAmount, commissionAmount, rate: r };
}

// ✅ مجموع «مبلغ خالص» قابل پرداخت به معلم از یک دوره
//    اول از snapshot هر ثبت‌نام استفاده می‌کنه،
//    اگه نبود (داده‌های قدیمی)، با نرخ فعلی حساب می‌کنه
export function computeCourseNetPending(course, adminSettings) {
  let total = 0;
  const students = course.enrolledStudents || [];
  for (const s of students) {
    if (typeof s.netToTeacher === 'number') {
      total += s.netToTeacher;
    } else {
      const rate = getCommissionRate(adminSettings);
      const { netAmount } = computeCommissionSplit(course.price || 0, rate);
      total += netAmount;
    }
  }
  return total;
}

// ✅ مجموع «کارمزد سکو» قابل کسب از یک دوره (برای pending commission)
export function computeCourseCommissionPending(course, adminSettings) {
  let total = 0;
  const students = course.enrolledStudents || [];
  for (const s of students) {
    if (typeof s.commissionAmount === 'number') {
      total += s.commissionAmount;
    } else {
      const rate = getCommissionRate(adminSettings);
      const { commissionAmount } = computeCommissionSplit(course.price || 0, rate);
      total += commissionAmount;
    }
  }
  return total;
}














// ================================================================
// ✅ ثابت‌های مالی — VAT و جریمه انصراف
// ================================================================
export const VAT_RATE = 10;
export const CANCEL_PENALTY_RATE = 10;

// ✅ تقسیم پرداخت ثبت‌نام — totalPaid = price + VAT
export function computePaymentSplit(price, commissionRate, vatRate = VAT_RATE) {
  const base = Number(price) || 0;
  const cRate = Number(commissionRate) || 0;
  const vRate = Number(vatRate) || 0;
  const vatAmount = Math.round(base * (vRate / 100));
  const totalPaid = base + vatAmount;
  const commissionAmount = Math.round(base * (cRate / 100));
  const netToTeacher = base - commissionAmount;
  return {
    base, vatAmount, totalPaid,
    commissionAmount, netToTeacher,
    commissionRate: cRate, vatRate: vRate,
  };
}

// ✅ تقسیم انصراف دانش‌آموز — VAT + جریمه پیش سکو می‌مونن
export function computeCancelSplit(price, penaltyRate = CANCEL_PENALTY_RATE, vatRate = VAT_RATE) {
  const base = Number(price) || 0;
  const pRate = Number(penaltyRate) || 0;
  const vRate = Number(vatRate) || 0;
  const vatAmount = Math.round(base * (vRate / 100));
  const totalPaid = base + vatAmount;
  const penaltyAmount = Math.round(base * (pRate / 100));
  const refundAmount = base - penaltyAmount;
  return { base, vatAmount, totalPaid, penaltyAmount, refundAmount };
}












// ✅ ثابت‌های زمانی
const GRACE_PERIOD_MS = 24000;              // ۲۴ ثانیه بی‌خبری از Roomeet
const AUTO_END_BUFFER_MIN = 15;             // ۱۵ دقیقه بافر بعد از مدت رسمی
const SCHEDULED_AUTO_CANCEL_HOURS = 6;      // ۶ ساعت بعد از زمان شروع → لغو خودکار
const START_LOCK_MS = 15000;                // قفل شروع ۱۵ ثانیه‌ای
const ENROLL_LOCK_MS = 8000;                // قفل ثبت‌نام ۸ ثانیه‌ای

// ================================================================
// ۲. Theme Context
// ================================================================
const ThemeContext = createContext({ theme: 'light', toggleTheme: () => {} });
export const useTheme = () =>
  useContext(ThemeContext) || { theme: 'light', toggleTheme: () => {} };

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem('navbar2-drawer-dark') === '1' ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  });

  // ذخیره در کلید مشترک Navbar2 + اعمال روی html
  useEffect(() => {
    try {
      localStorage.setItem('navbar2-drawer-dark', theme === 'dark' ? '1' : '0');
      document.documentElement.setAttribute('data-theme', theme);
    } catch {}
  }, [theme]);

  // گوش دادن به تغییرات Navbar2
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'navbar2-drawer-dark') {
        setTheme(e.newValue === '1' ? 'dark' : 'light');
      }
    };
    window.addEventListener('storage', handleStorage);

    const interval = setInterval(() => {
      try {
        const current = localStorage.getItem('navbar2-drawer-dark') === '1' ? 'dark' : 'light';
        setTheme((prev) => (prev !== current ? current : prev));
      } catch {}
    }, 500);

    return () => {
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, []);

  const toggleTheme = useCallback(
    () => setTheme((p) => (p === 'light' ? 'dark' : 'light')),
    []
  );

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
ThemeProvider.propTypes = { children: PropTypes.node };

// ================================================================
// ۳. Toast Context — با Dedup
// ================================================================
const ToastContext = createContext();
export const useToast = () =>
  useContext(ToastContext) ||
  ((msg, type = 'info') => console.log(`[${type}] ${msg}`));

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());
  const recentRef = useRef(new Map()); // dedup cache

  const removeToast = useCallback((id) => {
    setToasts((p) => p.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  // ✅ F40: dedup پیام‌های تکراری در پنجره‌ی ۳ ثانیه‌ای
  const showToast = useCallback(
    (message, type = 'info', duration = 4000) => {
      const key = `${type}::${message}`;
      const now = Date.now();
      const last = recentRef.current.get(key) || 0;
      if (now - last < 3000) return; // skip duplicate
      recentRef.current.set(key, now);

      const id = now + Math.random();
      const icons = { success: '✅', error: '❌', info: 'ℹ️', warning: '⚠️' };
      setToasts((p) => [...p, { id, message, type, icon: icons[type] || 'ℹ️' }]);
      const timer = setTimeout(() => removeToast(id), duration);
      timersRef.current.set(id, timer);

      // cleanup old keys
      if (recentRef.current.size > 80) {
        const cutoff = now - 10000;
        recentRef.current.forEach((ts, k) => {
          if (ts < cutoff) recentRef.current.delete(k);
        });
      }
    },
    [removeToast]
  );

  useEffect(() => {
    const timers = timersRef.current;
    return () => { timers.forEach((t) => clearTimeout(t)); timers.clear(); };
  }, []);

  return (
    <ToastContext.Provider value={showToast}>
      {children}
      <div className="toast-container">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.type}`}>
            <span className="toast-icon">{t.icon}</span>
            <span>{t.message}</span>
            <button onClick={() => removeToast(t.id)}>✕</button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
ToastProvider.propTypes = { children: PropTypes.node };

// ================================================================
// ۴. Data Context & Reducer
// ================================================================
const DataContext = createContext();
export const useData = () => useContext(DataContext);

export const DEFAULT_ADMIN_SETTINGS = {
  commission: 10,
  maxStudents: 5,
  enableTeacherRegistration: true,
  defaultSaleId: '',
};

export const initialDataState = {
  courses: [],
  users: [],
  requests: [],
  transactions: [],
  wishlist: [],
  notifications: [],
  adminSettings: { ...DEFAULT_ADMIN_SETTINGS },
  loading: false,
  error: null,
};

export function dataReducer(state, action) {
  switch (action.type) {
    case 'SET_DATA':
      return { ...state, ...action.payload, loading: false, error: null };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'ADD_COURSE':
      return { ...state, courses: [...state.courses, action.payload] };
    case 'UPDATE_COURSE':
      return {
        ...state,
        courses: state.courses.map((c) =>
          c.id === action.payload.id ? { ...c, ...action.payload } : c
        ),
      };
    case 'DELETE_COURSE':
      return { ...state, courses: state.courses.filter((c) => c.id !== action.payload) };
    case 'ADD_REQUEST':
      return { ...state, requests: [...state.requests, action.payload] };
    case 'UPDATE_REQUEST':
      return {
        ...state,
        requests: state.requests.map((r) =>
          r.id === action.payload.id ? { ...r, ...action.payload } : r
        ),
      };
    case 'DELETE_REQUEST':
      return { ...state, requests: state.requests.filter((r) => r.id !== action.payload) };
    case 'UPDATE_USER':
      return {
        ...state,
        users: state.users.map((u) =>
          u.id === action.payload.id ? { ...u, ...action.payload } : u
        ),
      };
    case 'ADD_USER':
      return { ...state, users: [...state.users, action.payload] };
    case 'DELETE_USER':
      return { ...state, users: state.users.filter((u) => u.id !== action.payload) };
    case 'ADD_TRANSACTION':
      return { ...state, transactions: [...state.transactions, action.payload] };
    case 'TOGGLE_WISHLIST':
      return { ...state, wishlist: action.payload };
    case 'ADD_NOTIFICATION':
      return { ...state, notifications: [action.payload, ...state.notifications] };
    case 'MARK_NOTIFICATION_READ':
      return {
        ...state,
        notifications: state.notifications.map((n) =>
          n.id === action.payload ? { ...n, read: true } : n
        ),
      };
    case 'UPDATE_SETTINGS':
      return {
        ...state,
        adminSettings: { ...state.adminSettings, ...action.payload },
      };
    default:
      return state;
  }
}

// ================================================================
// ۴.۵. Auth Errors & Helpers & Credentials
// ================================================================
export class RoomeetAuthError extends Error {
  constructor(message = 'احراز هویت Roomeet نامعتبر است') {
    super(message);
    this.name = 'RoomeetAuthError';
    this.isAuthError = true;
  }
}

export function getUserDisplayName(user) {
  if (!user) return 'کاربر';
  if (user.fullName) return user.fullName;
  if (user.name) return user.name;
  if (user.firstName || user.lastName) {
    return [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || 'کاربر';
  }
  return 'کاربر';
}

export function extractSchoolId(school) {
  if (!school) return null;
  return (
    school.schoolId || school._id || school.id ||
    school.school?._id || school.school?.id || school.school?.schoolId || null
  );
}

export function extractRole(school) {
  if (!school) return null;
  return school.role || null;
}

const ROOMEET_CREDS_KEY = 'roomeet_credentials';

export function saveRoomeetCreds(phone, password) {
  // ✅ اصلاح: رمز عبور در مرورگر ذخیره نمی‌شود.
  // احراز هویت از طریق توکن ذخیره‌شده در دیتابیس (roomeetToken) انجام می‌شود.
  try {
    localStorage.removeItem(ROOMEET_CREDS_KEY);
    sessionStorage.removeItem(ROOMEET_CREDS_KEY);
  } catch (_) {}
}
export function getRoomeetCreds() {
  // ✅ اصلاح: اطلاعات ورود از مرورگر بازیابی نمی‌شود.
  // این کار امنیت را افزایش داده و از وابستگی به کش مرورگر جلوگیری می‌کند.
  return null;
}
export function clearRoomeetCreds() {
  try {
    localStorage.removeItem(ROOMEET_CREDS_KEY);
    sessionStorage.removeItem(ROOMEET_CREDS_KEY);
  } catch (_) {}
}

const ROOMEET_AUTH_KEY = 'roomeet_auth_v1';

// ================================================================
// ۴.۶. ✅ Lock helpers (localStorage-based — cross-tab safe)
// ================================================================
function acquireLock(key, ms) {
  try {
    const now = Date.now();
    const last = Number(localStorage.getItem(key) || 0);
    if (now - last < ms) return false;
    localStorage.setItem(key, String(now));
    return true;
  } catch (_) { return true; }
}

function releaseLock(key) {
  try { localStorage.removeItem(key); } catch (_) {}
}

// ================================================================
// ۵. RoomeetAPIService
// ================================================================
export class RoomeetAPIService {
  constructor(options = {}) {
    // ✅ حذف شد: کلید API فقط در سرور است، نه فرانت‌اند
    // this.apiKey = ROOMEET_API_KEY;
    this.token = null;
    this.schoolId = null;
    this.userId = null;
    this.role = null;
    this.onAuthError = null;
    this._persist = options.persist !== false;
    this._restore = options.restore !== false;
    if (this._restore) this._restoreFromStorage();
  }

  _restoreFromStorage() {
    try {
      const raw = localStorage.getItem(ROOMEET_AUTH_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const { token, schoolId, userId, role, expiresAt } = parsed;
      if (token && (!expiresAt || Date.now() < expiresAt)) {
        this.token = token;
        this.schoolId = schoolId || null;
        this.userId = userId || null;
        this.role = role || null;
      } else {
        try { localStorage.removeItem(ROOMEET_AUTH_KEY); } catch (_) {}
      }
    } catch (_) {}
  }

  _persistToStorage() {
    if (!this._persist) return;
    try {
      if (!this.token) { localStorage.removeItem(ROOMEET_AUTH_KEY); return; }
      let expiresAt = null;
      try {
        const payload = JSON.parse(atob(this.token.split('.')[1]));
        if (payload.exp) expiresAt = payload.exp * 1000;
      } catch (_) {}
      localStorage.setItem(
        ROOMEET_AUTH_KEY,
        JSON.stringify({
          token: this.token, schoolId: this.schoolId || null,
          userId: this.userId, role: this.role || null, expiresAt,
        })
      );
    } catch (_) {}
  }

  setAuth(token, schoolId, userId = null, role = null) {
    this.token = token;
    this.schoolId = schoolId || null;
    if (userId !== null) this.userId = userId;
    if (role !== null) this.role = role;
    this._persistToStorage();
  }

  clearAuth() {
    this.token = null;
    this.schoolId = null;
    this.userId = null;
    this.role = null;
    if (this._persist) {
      try { localStorage.removeItem(ROOMEET_AUTH_KEY); } catch (_) {}
    }
  }

  isAuthenticated() {
    if (!this.token) return false;
    try {
      const payload = JSON.parse(atob(this.token.split('.')[1]));
      if (payload.exp && Date.now() >= payload.exp * 1000) {
        this.clearAuth();
        return false;
      }
    } catch (_) {}
    return true;
  }

  isManager() {
    return this.role === 'manager' || this.role === 'admin';
  }

  async _call(endpoint, method = 'GET', body = null, queryParams = {}) {
    if (
      endpoint.startsWith('/school/') &&
      this.role && this.role !== 'manager' && this.role !== 'admin'
    ) {
      throw new Error(`دسترسی به ${endpoint} فقط برای مدیر مدرسه مجاز است.`);
    }

    const queryParts = [];
    if (this.schoolId) queryParts.push(`schoolId=${this.schoolId}`);
    // ✅ خط مربوط به api_key حذف شد (سرور خودش اضافه می‌کند)

    Object.entries(queryParams).forEach(([k, v]) => {
      if (v !== undefined && v !== null) {
        queryParts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
      }
    });

    const qs = queryParts.join('&');
    // ✅ ROOMEET_BASE حالا به /api/roomeet اشاره می‌کند
    const url = `${ROOMEET_BASE}${endpoint}${qs ? '?' + qs : ''}`;

    const headers = { 'Content-Type': 'application/json' };
    
    // ✅ اگر کاربر معمولی (دانش‌آموز/معلم) لاگین کرده باشد، توکن خودش را می‌فرستد.
    // اگر توکن نداشته باشد، سرور به صورت خودکار از توکن ادمین استفاده می‌کند.
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;

    const options = { method, headers };
    if (body) options.body = JSON.stringify(body);

    try {
      const res = await fetch(url, options);

      if (res.status === 401 || res.status === 403) {
        this.clearAuth();
        if (typeof this.onAuthError === 'function') {
          try { this.onAuthError(); } catch (_) {}
        }
        throw new RoomeetAuthError(
          `دسترسی به Roomeet رد شد (${res.status}). لطفاً دوباره وارد شوید.`
        );
      }

      const text = await res.text();
      if (text.trim().startsWith('<')) {
        if (res.status === 404) {
          throw new Error('اتصال به Roomeet نامعتبر است. API Key و JWT را بررسی کنید.');
        }
        throw new Error(`پاسخ HTML از Roomeet (کد ${res.status})`);
      }

      let data;
      try { data = JSON.parse(text); } catch (_) {
        throw new Error(`JSON نیست (کد ${res.status}): ${text.substring(0, 200)}`);
      }

      if (!res.ok) {
        const errorMsg = data.message || data.error || `Error ${res.status}`;
        throw new Error(`${res.status} - ${errorMsg}`);
      }
      return data;
    } catch (err) {
      if (err instanceof RoomeetAuthError) throw err;
      if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        throw new Error('خطای شبکه. اتصال اینترنت را بررسی کنید.');
      }
      throw err;
    }
  }

  async login(phone, password) {
    try {
      const result = await this._call('/auth/login', 'POST', { phone, password });
      if (result.token) {
        const school = result.schools?.[0] || {};
        this.setAuth(result.token, extractSchoolId(school), null, extractRole(school));
        return result;
      }
      throw new Error('پاسخ سرور فاقد توکن است.');
    } catch (err) {
      if (err instanceof RoomeetAuthError) throw err;
      if (err.message.includes('401')) {
        throw new Error('شماره تلفن یا رمز عبور اشتباه است.');
      } else if (err.message.includes('500')) {
        throw new Error('خطای داخلی سرور رومیت (500). با پشتیبانی تماس بگیرید.');
      }
      throw err;
    }
  }

  async getSchoolInfo() { return this._call('/school/info'); }

  async getSchoolMembers(page = 1, countPerPage = 100, role = '', keyword = '') {
    return this._call('/school/members', 'GET', null, { page, countPerPage, role, keyword });
  }

  async getServices() {
    // ✅ هدایت درخواست به پروکسی سرور خودمان
    return this._call('/school/services');
  }

  async addUsersToSchool(users, role) {
    return this._call('/school/add-users', 'POST', { users, role });
  }

  async updateUserInSchool(memberId, name, password) {
    return this._call('/school/update-user', 'POST', { memberId, name, password });
  }

  async removeMember(memberId) {
    return this._call('/school/remove-member', 'POST', { memberId });
  }

  async getAllRooms(page = 1, countPerPage = 10, keyword = '') {
    const endpoint = this.isManager() ? '/school/room/fetch-all' : '/user/rooms';
    return this._call(endpoint, 'GET', null, { page, countPerPage, keyword });
  }

  async createRoom(saleId, data) {
    return this._call('/school/room/create', 'POST', data, { saleId });
  }

  async updateRoom(theIdOfMeeting, saleId, data) {
    const endpoint = this.isManager() ? '/school/room/update' : '/user/update-room';
    return this._call(endpoint, 'POST', data, { saleId, theIdOfMeeting });
  }

  async removeRoom(theIdOfMeeting) {
    return this._call('/school/room/remove', 'POST', null, { theIdOfMeeting });
  }

  async startRoom(theIdOfMeeting) {
    const endpoint = this.isManager() ? '/school/room/start' : '/user/start-room';
    return this._call(endpoint, 'POST', null, { theIdOfMeeting });
  }

  async endRoom(theIdOfMeeting) {
    const endpoint = this.isManager() ? '/school/room/end' : '/user/end-room';
    return this._call(endpoint, 'POST', null, { theIdOfMeeting });
  }

  async getOnlineRooms(saleId = null) {
    const endpoint = this.isManager() ? '/school/room/online-rooms' : '/user/online-rooms';
    return this._call(endpoint, 'GET', null, { saleId });
  }

  async getRoomMembers(theIdOfMeeting, role = '', page = 1, countPerPage = 100) {
    return this._call('/school/room/members', 'GET', null, {
      theIdOfMeeting, page, countPerPage, role,
    });
  }

  async getRoomNotMembers(theIdOfMeeting, role = '', page = 1, countPerPage = 100) {
    return this._call('/school/room/not-members', 'GET', null, {
      theIdOfMeeting, page, countPerPage, role,
    });
  }

  async addMembersToRoom(theIdOfMeeting, members) {
    return this._call('/school/room/add-members', 'POST', { members }, { theIdOfMeeting });
  }

  async removeMemberFromRoom(theIdOfMeeting, memberId) {
    return this._call('/school/room/remove-member', 'POST', { memberId }, { theIdOfMeeting });
  }

  async insertAllByRole(theIdOfMeeting, role) {
    return this._call('/school/room/insert-all-by-role', 'POST', { role }, { theIdOfMeeting });
  }

  async getRoomRecordings(theIdOfMeeting) {
    const endpoint = this.isManager() ? '/school/room/recordings' : '/user/recordings';
    return this._call(endpoint, 'GET', null, { theIdOfMeeting });
  }

  async publishRecording(recordId, publish) {
    return this._call('/school/publish-recording', 'POST', { recordId, publish });
  }

  async deleteRecording(recordId, serverId) {
    return this._call('/school/delete-recording', 'POST', { recordId, serverId });
  }

  async joinRoom(theIdOfMeeting) {
    return this._call('/user/join-room', 'GET', null, { theIdOfMeeting });
  }

  async getGuestJoinLink(theIdOfMeeting, fullName) {
    return {
      link: `${ROOMEET_PUBLIC_URL}/join/${theIdOfMeeting}?guest=true&fullName=${encodeURIComponent(fullName)}`,
    };
  }
}

// ================================================================
// ۵.۵. Admin API Helper — ✅ D28: چک انقضا
// ================================================================
// ✅ تغییر یافت: دیگر در فرانت‌اند لاگین نمی‌کنیم. فقط یک نمونه می‌سازیم تا درخواست‌ها را به سرور بفرستد.
// سرور ما به صورت خودکار با ادمین لاگین می‌شود و توکن را در پشت صحنه استفاده می‌کند.
export async function getAdminRoomeetApi(forceRelogin = false) {
  const api = new RoomeetAPIService({ persist: false, restore: false });
  api.role = 'manager'; // نقش را مدیر تنظیم می‌کنیم تا دسترسی‌ها باز باشد
  return api;
}

// ================================================================
// ۶. InternalAPIService — قلب سیستم
// ================================================================
export class InternalAPIService {
constructor(roomeetApi) {
  this._cache = null;
  this._lastFetch = 0;
  this._cacheTTL = 3000;
  this.roomeetApi = roomeetApi;
  this._syncInFlight = false;
  // ✅ نگهداری وضعیت آخرین ذخیره‌ی هر کاربر (برای ذخیره‌ی فقط تغییر‌یافته‌ها)
  this._lastSavedUserMap = new Map();
}

  // ------------------------------------------------------------
  // _fetchData — با نرمال‌سازی + ✅ A9 cleanup orphan enrolledCourses
  // ------------------------------------------------------------
async _fetchData(force = false) {
  const now = Date.now();
  if (!force && this._cache && now - this._lastFetch < this._cacheTTL) {
    return this._cache;
  }
  try {
    // ✅ منبع حقیقت: users از کالکشن users، بقیه از appData
    const token = getToken();
    const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};
    
    const [appDataRes, usersRes] = await Promise.all([
      fetch(`${API_BASE_URL}/appData`, { headers: authHeaders }),
      fetch(`${API_BASE_URL}/users`, { headers: authHeaders }),
    ]);
    if (!appDataRes.ok) throw new Error('Failed to fetch appData');
    if (!usersRes.ok) throw new Error('Failed to fetch users');

    const appDataRaw = await appDataRes.json();
    const usersRaw = await usersRes.json();
    const usersFromCollection = Array.isArray(usersRaw) ? usersRaw : [];

    const data = {
      courses: appDataRaw.courses || [],
      users: usersFromCollection,   // ← از کالکشن /users
      requests: appDataRaw.requests || [],
      transactions: appDataRaw.transactions || [],
      wishlist: appDataRaw.wishlist || [],
      notifications: appDataRaw.notifications || [],
      adminSettings: appDataRaw.adminSettings || { ...DEFAULT_ADMIN_SETTINGS },
    };

    // نرمال‌سازی وضعیت‌های قدیمی
    data.courses = data.courses.map((c) => {
      let s = c.status;
      if (s === 'approved') s = COURSE_STATUS.SCHEDULED;
      if (s === 'started') s = COURSE_STATUS.LIVE;
      const {
        meetingStatus: _ms,
        startAttemptCount: _sac,
        errorMessage: _em,
        lastStartAttempt: _lsa,
        ...clean
      } = c;
      return s !== c.status ? { ...clean, status: s } : clean;
    });

    // ✅ A9: پاک‌سازی enrolledCourses اشاره‌گر به کلاس حذف‌شده
    const validCourseIds = new Set(data.courses.map((c) => c.id));
    data.users = data.users.map((u) => {
      const { pendingRoomMembership: _prm, ...clean } = u;
      if (clean.enrolledCourses && clean.enrolledCourses.length > 0) {
        const filtered = clean.enrolledCourses.filter((id) => validCourseIds.has(id));
        if (filtered.length !== clean.enrolledCourses.length) {
          return { ...clean, enrolledCourses: filtered };
        }
      }
      return clean;
    });

    this._cache = data;
    this._lastFetch = now;
    try { localStorage.setItem('appData', JSON.stringify(data)); } catch (_) {}
    return data;
  } catch (e) {
    try {
      const cached = localStorage.getItem('appData');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed.courses) {
          this._cache = parsed;
          this._lastFetch = now;
          return parsed;
        }
      }
    } catch (_) {}
    throw e;
  }
}

async _saveData(data) {
  try {
    // ✅ ۱) ذخیره‌ی غیر-کاربران در /appData
    const { users = [], ...rest } = data;
    const token = getToken();
    const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};

    const appDataRes = await fetch(`${API_BASE_URL}/appData`, {
      method: 'PUT',
      headers: { 
        'Content-Type': 'application/json',
        ...authHeaders
      },
      body: JSON.stringify({
        ...rest,
        settings: (this._cache && this._cache.settings) || {},
        version: (this._cache && this._cache.version) || '1.0',
      }),
    });
    if (!appDataRes.ok) {
      throw new Error(`Server responded with ${appDataRes.status} (appData)`);
    }

    // ✅ ۲) ذخیره‌ی کاربران در کالکشن /users — فقط آن‌هایی که تغییر کرده‌اند
    const currentUserIds = new Set(users.map((u) => String(u.id)));

    for (const u of users) {
      const uid = String(u.id);
      const json = JSON.stringify(u);
      const lastSaved = this._lastSavedUserMap.get(uid);

      if (lastSaved !== json) {
        // تلاش برای PATCH؛ اگر 404 شد → POST برای ساخت
        try {
const patchRes = await fetch(`${API_BASE_URL}/users/${u.id}`, {
  method: 'PATCH',
  headers: { 'Content-Type': 'application/json', ...authHeaders },
  body: json,
});
          if (patchRes.status === 404) {
await fetch(`${API_BASE_URL}/users`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json', ...authHeaders },
  body: json,
});
          }
          this._lastSavedUserMap.set(uid, json);
        } catch (perUserErr) {
          console.warn(`[saveUser:${uid}]`, perUserErr.message);
        }
      }
    }

    // ✅ ۳) حذف کاربرانی که از data حذف شده‌اند
    for (const uid of Array.from(this._lastSavedUserMap.keys())) {
      if (!currentUserIds.has(uid)) {
        try {
          await fetch(`${API_BASE_URL}/users/${uid}`, { method: 'DELETE', headers: authHeaders });
        } catch (_) {}
        this._lastSavedUserMap.delete(uid);
      }
    }
  } catch (e) {
    console.error('⚠️ خطا در ارسال به سرور:', e);
    throw new Error('ذخیره‌سازی در دیتابیس ناموفق بود. لطفاً اتصال اینترنت خود را بررسی کنید.');
  }

  this._cache = data;
  this._lastFetch = Date.now();
  try { localStorage.setItem('appData', JSON.stringify(data)); } catch (_) {}
}

  // ------------------------------------------------------------
  // ✅ Helper: کاهش pendingAmount معلم (در data موجود)
  // ------------------------------------------------------------
  _decreaseTeacherPendingInData(data, teacherId, amount) {
    if (!teacherId || !amount || amount <= 0) return;
    const teacher = data.users.find((u) => u.id === teacherId);
    if (teacher) {
      teacher.pendingAmount = Math.max(0, (teacher.pendingAmount || 0) - amount);
    }
  }

  // ------------------------------------------------------------
  // ✅ Helper: افزودن batch notifications (یک save)
  // ------------------------------------------------------------
  async _addNotificationsBatch(notifications) {
    if (!notifications || notifications.length === 0) return;
    const data = await this._fetchData(true);
    const now = moment().format('jYYYY-jMM-jDD');
    for (const n of notifications) {
      data.notifications.push({
        id: Date.now() + Math.random() + Math.random(),
        message: n.message,
        type: n.type || 'info',
        read: false,
        date: now,
        userId: n.userId || null,
      });
    }
    await this._saveData(data);
  }

  // ------------------------------------------------------------
  // ✅ Helper: notify ادمین‌ها (فقط به ادمین‌ها)
  // ------------------------------------------------------------
  async notifyAdmins(message, type = 'info', excludeUserId = null) {
    const data = await this._fetchData(true);
    const admins = data.users.filter(
      (u) => u.role === 'admin' && u.id !== excludeUserId
    );
    if (!admins.length) return;
    const now = moment().format('jYYYY-jMM-jDD');
    for (const admin of admins) {
      data.notifications.push({
        id: Date.now() + Math.random() + Math.random(),
        message, type, read: false, date: now, userId: admin.id,
      });
    }
    await this._saveData(data);
  }

  // ============================================================
  // تضمین وجود کاربر در Roomeet
  // ============================================================
  async ensureUserInRoomeet(userId, preloadedData = null, apiOverride = null) {
    const api = apiOverride || this.roomeetApi;
    if (!api) return null;

    if (!apiOverride && typeof api.isManager === 'function' && !api.isManager()) {
      return null;
    }

    const data = preloadedData || (await this._fetchData(true));
    const user = data.users.find((u) => u.id === userId);
    if (!user) return null;
    if (user.roomeetMemberId) return user.roomeetMemberId;

    const phoneWithoutZero = user.phone?.startsWith('0')
      ? user.phone.substring(1) : user.phone;

    try {
      const searchResult = await api.getSchoolMembers(1, 50, user.role, phoneWithoutZero);
      const found = searchResult.members?.find(
        (m) =>
          m.phone === user.phone ||
          m.phone === phoneWithoutZero ||
          m.phone === `0${phoneWithoutZero}`
      );
      if (found) {
        user.roomeetMemberId = found.memberId;
        if (!preloadedData) await this._saveData(data);
        return found.memberId;
      }
    } catch (e) {
      console.warn('[ensureUserInRoomeet] search خطا:', e.message);
    }

    try {
      const role = user.role === 'teacher' ? 'teacher' : 'student';
      const result = await api.addUsersToSchool(
        [{ name: getUserDisplayName(user), phone: user.phone, password: user.password || '123456' }],
        role
      );
      const added = result.users?.find((u) => u.error === false);
      if (added?.memberId) {
        user.roomeetMemberId = added.memberId;
        if (!preloadedData) await this._saveData(data);
        return added.memberId;
      }
      if (result.users?.[0]?.error === true) {
        const again = await api.getSchoolMembers(1, 50, role, phoneWithoutZero);
        const found2 = again.members?.find(
          (m) => m.phone === user.phone || m.phone === phoneWithoutZero
        );
        if (found2?.memberId) {
          user.roomeetMemberId = found2.memberId;
          if (!preloadedData) await this._saveData(data);
          return found2.memberId;
        }
      }
      return null;
    } catch (e) {
      console.error('[ensureUserInRoomeet] ❌:', e.message);
      return null;
    }
  }

  async ensureUserInRoom(userId, theIdOfMeeting, preloadedData = null, apiOverride = null) {
    const api = apiOverride || this.roomeetApi;
    if (!api || !theIdOfMeeting) return false;

    if (!apiOverride && typeof api.isManager === 'function' && !api.isManager()) {
      return false;
    }

    const memberId = await this.ensureUserInRoomeet(userId, preloadedData, api);
    if (!memberId) return false;

    const data = preloadedData || (await this._fetchData(true));
    const user = data.users.find((u) => u.id === userId);

    try {
      await api.addMembersToRoom(theIdOfMeeting, [
        { memberId, role: user?.role === 'teacher' ? 'teacher' : 'student' },
      ]);
      return true;
    } catch (e) {
      if (e.message?.includes('already') || e.message?.includes('exist')) return true;
      console.error(`[ensureUserInRoom] ❌:`, e.message);
      return false;
    }
  }

  async _ensureWithAdmin(userId, preloadedData = null) {
    try {
      const adminApi = await getAdminRoomeetApi();
      if (!adminApi) return null;
      return await this.ensureUserInRoomeet(userId, preloadedData, adminApi);
    } catch (e) {
      console.warn('[_ensureWithAdmin] خطا:', e.message);
      return null;
    }
  }

  async _ensureRoomWithAdmin(userId, roomId, preloadedData = null) {
    try {
      const adminApi = await getAdminRoomeetApi();
      if (!adminApi) return false;
      return await this.ensureUserInRoom(userId, roomId, preloadedData, adminApi);
    } catch (e) {
      console.warn('[_ensureRoomWithAdmin] خطا:', e.message);
      return false;
    }
  }

  // ============================================================
  // ✅ startCourse — با Lock و post-fetch validate
  // حل C20 (race start), C22 (race cancel+start)
  // ============================================================
  async startCourse(courseId, byRole = 'teacher') {
    // ✅ C20: قفل سراسری برای جلوگیری از شروع همزمان
    const LOCK_KEY = `roomeet_start_lock_${courseId}`;
    if (!acquireLock(LOCK_KEY, START_LOCK_MS)) {
      throw new Error('کلاس همین الان توسط کاربر دیگری در حال شروع است. لطفاً صبر کنید.');
    }

    try {
      const data = await this._fetchData(true);
      const idx = data.courses.findIndex((c) => c.id === courseId);
      if (idx === -1) throw new Error('کلاس یافت نشد');
      const course = data.courses[idx];

      // ✅ C22: چک دوباره بعد از fetch
      if (!course.siroomehClassId) throw new Error('کلاس هنوز در رومیت ساخته نشده است');
      if (course.status === COURSE_STATUS.CANCELLED) throw new Error('این کلاس لغو شده است');
      if (course.status === COURSE_STATUS.ENDED) throw new Error('این کلاس به پایان رسیده است');
      if (course.status === COURSE_STATUS.LIVE) throw new Error('کلاس قبلاً شروع شده است');
      if (course.status !== COURSE_STATUS.SCHEDULED) {
        throw new Error(`وضعیت کلاس نامعتبر است (${course.status})`);
      }

      const adminApi = await getAdminRoomeetApi();
      if (!adminApi) throw new Error('اتصال به Roomeet برقرار نشد');

      // ۱. استارت اتاق در Roomeet
      const startResult = await adminApi.startRoom(course.siroomehClassId);
      if (startResult?.taskStatus !== 'SUCCESS') {
        throw new Error(`startRoom ناموفق: ${startResult?.taskStatus || 'no response'}`);
      }

      // ۲. افزودن معلم + دانش‌آموزان به اتاق
      if (course.teacherId) {
        await this._ensureRoomWithAdmin(course.teacherId, course.siroomehClassId, data);
      }
      for (const s of (course.enrolledStudents || [])) {
        await this._ensureRoomWithAdmin(s.userId, course.siroomehClassId, data);
      }

      // ۳. آپدیت دیتابیس
      const now = moment();
      data.courses[idx] = {
        ...course,
        status: COURSE_STATUS.LIVE,
        startedAt: course.startedAt || now.format('jYYYY-jMM-jDD HH:mm'),
        startedBy: course.startedBy || byRole,
        roomIsLive: true,
        roomOfflineSince: null,
      };
      await this._saveData(data);

      // ۴. آزادسازی پرداخت معلم
      try { await this.releasePendingPayment(courseId); } catch (_) {}

      // ۵. نوتیفیکیشن (batch)
      const notifs = [
        { message: `⏰ کلاس "${course.title}" شروع شد.`, type: 'info', userId: course.teacherId },
      ];
      for (const s of (course.enrolledStudents || [])) {
        notifs.push({
          message: `⏰ کلاس "${course.title}" شروع شد. وارد پنل شوید و روی «ورود به کلاس» بزنید.`,
          type: 'success',
          userId: s.userId,
        });
      }
      // ✅ E38: به ادمین‌های دیگر هم اطلاع
      const admins = data.users.filter(
        (u) => u.role === 'admin' && u.id !== course.teacherId
      );
      for (const admin of admins) {
        notifs.push({
          message: `🟢 کلاس "${course.title}" شروع شد.`,
          type: 'info',
          userId: admin.id,
        });
      }
      try { await this._addNotificationsBatch(notifs); } catch (_) {}

      return { success: true, course: data.courses[idx] };
    } finally {
      setTimeout(() => releaseLock(LOCK_KEY), 3000);
    }
  }

  // ============================================================
  // ✅ endCourse — با validate سختگیرانه (حل A6)
  // ============================================================
  async endCourse(courseId, byRole = 'teacher', reason = 'manual') {
    const data = await this._fetchData(true);
    const idx = data.courses.findIndex((c) => c.id === courseId);
    if (idx === -1) throw new Error('کلاس یافت نشد');
    const course = data.courses[idx];

    // ✅ A6: validate سختگیرانه
    if (course.status === COURSE_STATUS.ENDED) {
      return { success: true, alreadyEnded: true, course };
    }
    if (course.status === COURSE_STATUS.CANCELLED) {
      throw new Error('این کلاس لغو شده است');
    }
    if (course.status === COURSE_STATUS.SCHEDULED) {
      throw new Error('کلاس هنوز شروع نشده است — امکان پایان دادن وجود ندارد');
    }
    if (course.status !== COURSE_STATUS.LIVE) {
      throw new Error(`وضعیت کلاس برای پایان نامعتبر است (${course.status})`);
    }

    // ۱. پایان اتاق در Roomeet
    if (course.siroomehClassId) {
      try {
        const adminApi = await getAdminRoomeetApi();
        if (adminApi) await adminApi.endRoom(course.siroomehClassId);
      } catch (e) {
        console.warn('[endCourse] endRoom خطا:', e.message);
      }
    }

    // ۲. آپدیت دیتابیس
    const now = moment();
    data.courses[idx] = {
      ...course,
      status: COURSE_STATUS.ENDED,
      endedAt: now.format('jYYYY-jMM-jDD HH:mm'),
      endedBy: byRole,
      endReason: reason,
      roomIsLive: false,
      roomOfflineSince: null,
    };
    await this._saveData(data);

    // ۳. نوتیفیکیشن (batch)
    const notifs = [
      { message: `🔚 کلاس "${course.title}" پایان یافت.`, type: 'info', userId: course.teacherId },
    ];
    for (const s of (course.enrolledStudents || [])) {
      notifs.push({
        message: `🔚 کلاس "${course.title}" به پایان رسید.`,
        type: 'info',
        userId: s.userId,
      });
    }
    const admins = data.users.filter((u) => u.role === 'admin');
    for (const admin of admins) {
      notifs.push({
        message: `🔚 کلاس "${course.title}" پایان یافت.`,
        type: 'info',
        userId: admin.id,
      });
    }
    try { await this._addNotificationsBatch(notifs); } catch (_) {}

    return { success: true, course: data.courses[idx] };
  }

  // ============================================================
  // ✅ joinCourse — با re-verify بعد از گرفتن لینک (حل C23)
  // ============================================================
  async joinCourse(courseId) {
    const data = await this._fetchData(true);
    const course = data.courses.find((c) => c.id === courseId);
    if (!course) throw new Error('کلاس یافت نشد');
    if (!course.siroomehClassId) throw new Error('کلاس هنوز در رومیت ساخته نشده است');
    if (course.status === COURSE_STATUS.CANCELLED) throw new Error('این کلاس لغو شده است');
    if (course.status === COURSE_STATUS.ENDED) throw new Error('این کلاس به پایان رسیده است');
    if (course.status !== COURSE_STATUS.LIVE) throw new Error('کلاس هنوز شروع نشده است');

    const result = await this.roomeetApi.joinRoom(course.siroomehClassId);
    if (!result?.link) throw new Error('لینک ورود دریافت نشد');

    // ✅ C23: بعد از گرفتن لینک، یه بار دیگه وضعیت رو چک کن
    try {
      const fresh = await this._fetchData(true);
      const freshCourse = fresh.courses.find((c) => c.id === courseId);
      if (!freshCourse || freshCourse.status !== COURSE_STATUS.LIVE) {
        throw new Error('کلاس در همین لحظه پایان یافت. لطفاً صفحه را رفرش کنید.');
      }
    } catch (e) {
      if (e.message.includes('پایان یافت')) throw e;
      // ignore other errors
    }

    return { success: true, link: result.link };
  }

  // ============================================================
  // ✅ syncRooms — با lock، release payment، duration sync، auto-cancel
  // حل D29 (pile-up)، D30 (duration drift)، B4/B34 (release payment)
  // ============================================================
  async syncRooms(onChange) {
    // ✅ D29: جلوگیری از pile-up
    if (this._syncInFlight) return;
    this._syncInFlight = true;

    try {
      const adminApi = await getAdminRoomeetApi();
      if (!adminApi) return;

      let onlineRes;
      try {
        onlineRes = await adminApi.getOnlineRooms();
      } catch (e) {
        return;
      }

      const onlineRooms = onlineRes?.onlineMeetings || [];
      const onlineIds = extractOnlineRoomIds(onlineRes);

      const data = await this._fetchData(true);
      const now = moment();
      const nowMs = Date.now();
      let needsSave = false;
      const coursesToRelease = [];   // 🆕 صف پرداخت‌ها

      // ─── ۱. آپدیت roomIsLive + duration sync ───
      for (const course of data.courses) {
        if (!course.siroomehClassId) continue;
        if (course.status === COURSE_STATUS.CANCELLED) continue;
        if (course.status === COURSE_STATUS.ENDED) continue;

        const isLive = onlineIds.has(String(course.siroomehClassId));
        const wasLive = !!course.roomIsLive;

        const onlineRoom = onlineRooms.find(
          (r) => String(getRoomId(r)) === String(course.siroomehClassId)
        );
        if (onlineRoom?.duration && onlineRoom.duration !== course.duration) {
          course.duration = onlineRoom.duration;
          needsSave = true;
        }

        if (isLive && !wasLive) {
          course.roomIsLive = true;

          course.roomEverLive = true;

          course.roomOfflineSince = null;
          if (course.status !== COURSE_STATUS.LIVE) {
            course.status = COURSE_STATUS.LIVE;
            if (!course.startedAt) {
              course.startedAt = now.format('jYYYY-jMM-jDD HH:mm');
              course.startedBy = course.startedBy || 'auto-detected';
            }
            // 🆕 صف کن به‌جای صدا زدن مستقیم
            coursesToRelease.push(course.id);
          }
          needsSave = true;
        } else if (!isLive && wasLive) {
          course.roomIsLive = false;
          if (!course.roomOfflineSince) {
            course.roomOfflineSince = nowMs;
          }
          needsSave = true;
        } else if (isLive && wasLive && course.roomOfflineSince) {
          course.roomOfflineSince = null;
          needsSave = true;
        }
      }

      // ─── ۲. Auto-end: اتاق ۲۴ ثانیه بسته بود ───
      {/*for (const course of data.courses) {
        if (course.status !== COURSE_STATUS.LIVE) continue;
        if (course.roomIsLive) continue;
        if (!course.roomOfflineSince) continue;
        if (nowMs - course.roomOfflineSince < GRACE_PERIOD_MS) continue;

        course.status = COURSE_STATUS.ENDED;
        course.endedAt = now.format('jYYYY-jMM-jDD HH:mm');
        course.endedBy = 'auto';
        course.endReason = 'room_closed';
        course.roomOfflineSince = null;
        needsSave = true;

        // ✅ E7: notify معلم + دانش‌آموزان
        try {
          const notifs = [
            { message: `🔚 کلاس "${course.title}" بسته شد.`, type: 'info', userId: course.teacherId },
          ];
          for (const s of (course.enrolledStudents || [])) {
            notifs.push({
              message: `🔚 کلاس "${course.title}" بسته شد.`,
              type: 'info',
              userId: s.userId,
            });
          }
          await this._addNotificationsBatch(notifs);
        } catch (_) {}
      }*/}

      // ─── ۳. Auto-end: timeout ───
      // ─── ۳. Auto-end: فقط بر اساس timeout ───
// ─── ✅ Auto-end وقتی Roomeet اتاق رو بسته ───
const pendingNotifications = [];   // 🆕 جمع‌آوری همه نوتیفیکیشن‌ها

for (const course of data.courses) {
  if (course.status !== COURSE_STATUS.LIVE) continue;
  if (course.roomIsLive) continue;
  if (!course.roomOfflineSince) continue;

  if (!course.roomEverLive) continue;

  if (nowMs - course.roomOfflineSince < GRACE_PERIOD_MS) continue;

  course.status = COURSE_STATUS.ENDED;
  course.endedAt = now.format('jYYYY-jMM-jDD HH:mm');
  course.endedBy = 'auto';
  course.endReason = 'room_closed';
  course.roomOfflineSince = null;
  needsSave = true;

  // 🆕 فقط جمع کن
  pendingNotifications.push({
    message: `🔚 کلاس "${course.title}" بسته شد.`,
    type: 'info',
    userId: course.teacherId,
  });
  for (const s of (course.enrolledStudents || [])) {
    pendingNotifications.push({
      message: `🔚 کلاس "${course.title}" بسته شد.`,
      type: 'info',
      userId: s.userId,
    });
  }
}

      // ─── ۴. ✅ مشکل ۲: Auto-cancel کلاس‌های scheduled که شروع نشدند ───
      for (const course of data.courses) {
        if (course.status !== COURSE_STATUS.SCHEDULED) continue;
        if (!course.siroomehClassId) continue;
        const startMoment = getCourseStartMoment(course);
        if (!startMoment) continue;
        if (now.diff(startMoment, 'hours') <= SCHEDULED_AUTO_CANCEL_HOURS) continue;

        // auto-cancel + refund کامل
        const autoSnapshot = [...(course.enrolledStudents || [])];
        for (const student of autoSnapshot) {
          const user = data.users.find((u) => u.id === student.userId);
          if (user) {
            const fullRefund = typeof student.totalPaid === 'number'
              ? student.totalPaid
              : Math.round((course.price || 0) * (1 + VAT_RATE / 100));
            user.wallet = (user.wallet || 0) + fullRefund;
            if (user.enrolledCourses) {
              user.enrolledCourses = user.enrolledCourses.filter((id) => id !== course.id);
            }
            data.transactions.push({
              id: Date.now() + Math.random(),
              userId: user.id,
              amount: fullRefund,
              date: now.format('jYYYY-jMM-jDD'),
              type: 'refund',
              courseId: course.id,
              courseTitle: course.title,
              note: 'کلاس شروع نشد — لغو خودکار (بازگشت کامل)',
            });
          }
        }

        // ✅ B2: کاهش pendingAmount معلم (خالص)
        if (!course.pendingReleased) {
          const amount = computeCourseNetPending(course, data.adminSettings);
          this._decreaseTeacherPendingInData(data, course.teacherId, amount);
        }

      course.status = COURSE_STATUS.CANCELLED;
      course.cancelledAt = now.format('jYYYY-jMM-jDD HH:mm');
      course.cancelReason = 'کلاس شروع نشد — لغو خودکار توسط سیستم';
      course.refundProcessed = true;

      course.roomIsLive = false;
      course.roomOfflineSince = null;
      needsSave = true;


      pendingNotifications.push({
        message: `❌ کلاس "${course.title}" لغو شد (شروع نشد).`,
        type: 'warning',
        userId: course.teacherId,
      });
      for (const s of autoSnapshot) {   // ← ✅ درست شد
        pendingNotifications.push({
          message: `❌ کلاس "${course.title}" لغو شد و وجه شما بازگشت.`,
          type: 'warning',
          userId: s.userId,
        });
      }
    }

    // 🆕 ۱) اول همه تغییرات رو save کن
    if (needsSave) {
      await this._saveData(data);
      if (onChange) onChange(data.courses);
    }

    // 🆕 ۲) بعد پرداخت‌ها رو آزاد کن (روی داده‌ی تازه‌ی سرور)
    for (const id of coursesToRelease) {
      try { await this.releasePendingPayment(id); } catch (_) {}
    }

    // 🆕 ۳) در آخر همه نوتیفیکیشن‌ها رو یک‌جا بفرست
    if (pendingNotifications.length > 0) {
      try { await this._addNotificationsBatch(pendingNotifications); } catch (_) {}
    }
  } finally {
    this._syncInFlight = false;
  }
}

  // ============================================================
  // syncAllUsersToRoomeet
  // ============================================================
  async syncAllUsersToRoomeet(onProgress = null) {
    const adminApi = await getAdminRoomeetApi();
    if (!adminApi) {
      return {
        success: 0, failed: 0, total: 0,
        error: 'اتصال به ادمین Roomeet برقرار نشد',
      };
    }

    const data = await this._fetchData(true);
    const pending = data.users.filter(
      (u) => !u.roomeetMemberId && u.phone && u.role !== 'admin'
    );

    if (pending.length === 0) {
      return { success: 0, failed: 0, total: 0, allSynced: true };
    }

    let success = 0, failed = 0;
    const failedList = [];

    for (let i = 0; i < pending.length; i++) {
      const user = pending[i];
      try {
        const memberId = await this.ensureUserInRoomeet(user.id, data, adminApi);
        if (memberId) success++;
        else {
          failed++;
          failedList.push(`${user.name}(${user.phone})`);
        }
      } catch (e) {
        failed++;
        failedList.push(`${user.name}: ${e.message}`);
      }
      if (onProgress) onProgress(i + 1, pending.length, user.name);
    }

    if (success > 0) await this._saveData(data);
    return { success, failed, total: pending.length, failedList };
  }

  // ============================================================
  // syncAllRoomMemberships
  // ============================================================
  async syncAllRoomMemberships(onProgress = null) {
    const adminApi = await getAdminRoomeetApi();
    if (!adminApi) {
      return { added: 0, failed: 0, total: 0, error: 'adminApi null' };
    }

    const data = await this._fetchData(true);
    const eligible = data.courses.filter(
      (c) => c.siroomehClassId && c.status !== COURSE_STATUS.CANCELLED
    );

    let added = 0, failed = 0, total = 0;

    for (const course of eligible) {
      const roomId = course.siroomehClassId;
      const members = [];

      if (course.teacherId) {
        const teacherMid = await this.ensureUserInRoomeet(course.teacherId, data, adminApi);
        if (teacherMid) members.push({ memberId: teacherMid, role: 'teacher' });
      }

      for (const s of (course.enrolledStudents || [])) {
        const mid = await this.ensureUserInRoomeet(s.userId, data, adminApi);
        if (mid) members.push({ memberId: mid, role: 'student' });
      }

      if (!members.length) continue;
      total += members.length;
      if (onProgress) onProgress(course.title, members.length);

      try {
        const res = await adminApi.addMembersToRoom(roomId, members);
        added += res.addedMembersReport?.length || 0;
      } catch (e) {
        failed += members.length;
      }
    }

    if (added > 0) await this._saveData(data);
    return { added, failed, total };
  }

  // ============================================================
  // Getters
  // ============================================================
  async getCourses() { return (await this._fetchData()).courses; }
  async getUsers() { return (await this._fetchData()).users; }
  async getRequests() { return (await this._fetchData()).requests; }
  async getTransactions() { return (await this._fetchData()).transactions; }
  async getWishlist() { return (await this._fetchData()).wishlist || []; }
  async getNotifications() { return (await this._fetchData()).notifications || []; }
  async getAdminSettings() {
    return (await this._fetchData()).adminSettings || { ...DEFAULT_ADMIN_SETTINGS };
  }
  async getUserById(userId) {
    const users = await this.getUsers();
    return users.find((u) => u.id === userId);
  }
  async getStudentEnrolledCourses(studentId) {
    const user = await this.getUserById(studentId);
    if (!user || !user.enrolledCourses) return [];
    const courses = await this.getCourses();
    return courses.filter((c) => user.enrolledCourses.includes(c.id));
  }
  async getStudentProgress(studentId) {
    const enrolled = await this.getStudentEnrolledCourses(studentId);
    const active = enrolled.filter((c) => c.status !== COURSE_STATUS.CANCELLED);
    const total = active.length;
    const completed = active.filter(
      (c) =>
        c.status === COURSE_STATUS.ENDED ||
        moment(c.date, 'jYYYY-jMM-jDD').isBefore(moment(), 'day')
    ).length;
    return { total, completed, percent: total ? Math.round((completed / total) * 100) : 0 };
  }

  // ============================================================
  // Mutations
  // ============================================================
  async updateCourse(courseId, updates) {
    const data = await this._fetchData(true);
    const idx = data.courses.findIndex((c) => c.id === courseId);
    if (idx === -1) return false;
    data.courses[idx] = { ...data.courses[idx], ...updates };
    await this._saveData(data);
    return true;
  }

  async addCourse(course) {
    const data = await this._fetchData(true);
    const newCourse = {
      ...course,
      id: course.id || Date.now(),
      status: course.status || COURSE_STATUS.SCHEDULED,
      roomIsLive: false,
      roomOfflineSince: null,
      refundProcessed: false,
    };
    data.courses.push(newCourse);
    await this._saveData(data);

    if (newCourse.teacherId && newCourse.siroomehClassId) {
      try {
        await this._ensureRoomWithAdmin(
          newCourse.teacherId, newCourse.siroomehClassId, data
        );
        await this._saveData(data);
      } catch (e) {
        console.warn('[addCourse→Roomeet]', e.message);
      }
    }

    return newCourse;
  }

  async addRequest(request) {
    const data = await this._fetchData(true);
    const newReq = { id: Date.now(), ...request, status: request.status || 'pending' };
    data.requests.push(newReq);
    await this._saveData(data);
    return newReq;
  }

  async updateRequest(requestId, updates) {
    const data = await this._fetchData(true);
    const idx = data.requests.findIndex((r) => r.id === requestId);
    if (idx === -1) return false;
    data.requests[idx] = { ...data.requests[idx], ...updates };
    await this._saveData(data);
    return true;
  }

  async deleteRequest(requestId) {
    const data = await this._fetchData(true);
    data.requests = data.requests.filter((r) => r.id !== requestId);
    await this._saveData(data);
    return true;
  }

  // ============================================================
  // ✅ enrollStudent — با Lock و post-fetch validate (حل C21 overbooking)
  // ============================================================
  async enrollStudent(studentId, courseId) {
    try {
      // ✅ فقط درخواست ثبت‌نام را به بک‌اند می‌فرستیم. بقیه کارها با سرور است.
      const token = getToken();
      const res = await fetch('/api/actions/enroll', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ courseId })
      });

      const result = await res.json();
      
      if (!res.ok) {
        throw new Error(result.error || 'خطا در ثبت‌نام');
      }
      
      // فرانت‌اند دیتای خودش را آپدیت می‌کند تا کاربر تغییرات را ببیند
      await this._fetchData(true);
      return { success: true, message: result.message };
    } catch (error) {
      return { success: false, message: error.message };
    }
  }

  async toggleWishlist(studentId, courseId) {
    const data = await this._fetchData(true);
    let wishlist = data.wishlist || [];
    wishlist = wishlist.includes(courseId)
      ? wishlist.filter((id) => id !== courseId)
      : [...wishlist, courseId];
    data.wishlist = wishlist;
    await this._saveData(data);
    return wishlist;
  }

  async addTransaction(transaction) {
    const data = await this._fetchData(true);
    data.transactions.push({ id: Date.now() + Math.random(), ...transaction });
    await this._saveData(data);
    return true;
  }

  async updateUser(userId, updates) {
    const data = await this._fetchData(true);
    const idx = data.users.findIndex((u) => u.id === userId);
    if (idx === -1) return false;
    data.users[idx] = { ...data.users[idx], ...updates };
    await this._saveData(data);
    return true;
  }

  // 🆕 متد جدید: به‌روزرسانی یک تراکنش (برای تکمیل/رد درخواست برداشت)
  async updateTransaction(transactionId, updates) {
    const data = await this._fetchData(true);
    const idx = data.transactions.findIndex((t) => t.id === transactionId);
    if (idx === -1) return false;
    data.transactions[idx] = { ...data.transactions[idx], ...updates };
    await this._saveData(data);
    return true;
  }

  async updateAdminSettings(settings) {
    const data = await this._fetchData(true);
    data.adminSettings = { ...data.adminSettings, ...settings };
    await this._saveData(data);
    return true;
  }

  async addNotification(message, type = 'info', userId = null) {
    const data = await this._fetchData(true);
    const newNotif = {
      id: Date.now() + Math.random(), message, type, read: false,
      date: moment().format('jYYYY-jMM-jDD'), userId,
    };
    data.notifications.push(newNotif);
    await this._saveData(data);
    return newNotif;
  }

  async addUser(user) {
    const data = await this._fetchData(true);
    const newUser = { id: Date.now(), ...user };
    data.users.push(newUser);
    await this._saveData(data);

    if (newUser.role !== 'admin' && newUser.phone) {
      try {
        const adminApi = await getAdminRoomeetApi();
        if (adminApi) {
          const memberId = await this.ensureUserInRoomeet(newUser.id, data, adminApi);
          if (memberId) await this._saveData(data);
        }
      } catch (e) {
        console.error('[addUser→Roomeet]', e.message);
      }
    }

    return newUser;
  }

async deleteUser(userId) {
  const data = await this._fetchData(true);
  const user = data.users.find((u) => u.id === userId);

  // ───── ۱. حذف از Roomeet ─────
  if (user?.roomeetMemberId) {
    try {
      const adminApi = await getAdminRoomeetApi();
      if (adminApi) await adminApi.removeMember(user.roomeetMemberId);
    } catch (e) {
      console.warn('[deleteUser→Roomeet]', e.message);
    }
  }

  // ───── ۲. حذف از /userRewards (سکه، XP، جوایز) ─────
  try {
    const rwRes = await fetch(
      `${API_BASE_URL}/userRewards?userId=${encodeURIComponent(userId)}`
    );
    if (rwRes.ok) {
      const rewards = await rwRes.json();
      if (Array.isArray(rewards)) {
        await Promise.all(
          rewards.map((r) =>
            fetch(`${API_BASE_URL}/userRewards/${r.id}`, { method: 'DELETE' })
              .catch(() => {})
          )
        );
      }
    }
  } catch (e) {
    console.warn('[deleteUser→Rewards]', e.message);
  }

  // ───── ۳. حذف لایسنس‌های کتاب کار ─────
  try {
    const licRes = await fetch(
      `${API_BASE_URL}/workbookLicenses?userId=${encodeURIComponent(userId)}`
    );
    if (licRes.ok) {
      const lics = await licRes.json();
      if (Array.isArray(lics)) {
        await Promise.all(
          lics.map((l) =>
            fetch(`${API_BASE_URL}/workbookLicenses/${l.id}`, { method: 'DELETE' })
              .catch(() => {})
          )
        );
      }
    }
  } catch (e) {
    console.warn('[deleteUser→Licenses]', e.message);
  }

  // ───── ۴. ⚡ حذف صریح از /users collection — این خط حیاتی است ─────
  try {
    const res = await fetch(`${API_BASE_URL}/users/${userId}`, {
      method: 'DELETE',
    });
    if (!res.ok && res.status !== 404) {
      throw new Error(`DELETE /users/${userId} failed (${res.status})`);
    }
  } catch (e) {
    console.error('[deleteUser→Server]', e.message);
    throw e; // ← شکست واقعی، پس ادامه نده
  }

  // ───── ۵. پاکسازی داده‌های کاربر از appData (تراکنش‌ها، اعلان‌ها) ─────
  data.transactions = (data.transactions || []).filter(
    (t) => t.userId !== userId
  );
  data.notifications = (data.notifications || []).filter(
    (n) => n.userId !== userId
  );
  data.wishlist = (data.wishlist || []).filter((id) => id !== userId);
  data.requests = (data.requests || []).filter(
    (r) => r.teacherId !== userId
  );

  // ───── ۶. حذف از teamMembers (localStorage) ─────
  try {
    const members = engine.get('teamMembers', []);
    if (Array.isArray(members)) {
      const filtered = members.filter(
        (m) => m.studentId !== userId && m.teacherId !== userId
      );
      if (filtered.length !== members.length) {
        engine.set('teamMembers', filtered);
      }
    }
  } catch (e) {
    console.warn('[deleteUser→teamMembers]', e.message);
  }

  // ───── ۷. پاکسازی کلیدهای localStorage/IndexedDB مرتبط با این کاربر ─────
  try {
    const exactKeys = [
      `lesson-records-${userId}`,
      `curriculum_plans_${userId}`,
      `curriculum_plans-${userId}`,
      `user-stats-${userId}`,
      `streak-${userId}`,
      `dailyChallenge_${userId}`,
      `completedChallenges_${userId}`,
      `friends_${userId}`,
      `teacher-map-${userId}`,
    ];
    for (const k of exactKeys) {
      try { engine.remove(k); } catch {}
    }

    const prefixes = [
      `lesson-progress-${userId}-`,
      `step-quiz-progress-${userId}-`,
      `freestudy-progress-v2-${userId}-`,
      `freestudy-progress-${userId}-`,
      `workbook-license-${userId}-`,
      `celebration_seen_${userId}_`,
    ];
    for (const prefix of prefixes) {
      try {
        const keys = engine.keys(prefix);
        for (const k of keys) {
          try { engine.remove(k); } catch {}
        }
      } catch {}
    }
  } catch (e) {
    console.warn('[deleteUser→Storage]', e.message);
  }

  // ───── ۸. حذف از state حافظه + کش ─────
  data.users = data.users.filter((u) => u.id !== userId);
  this._lastSavedUserMap.delete(String(userId));

  // ───── ۹. ذخیره‌ی appData (بدون users) ─────
  await this._saveData(data);
  return true;
}

  // ============================================================
  // ✅ deleteCourse — حل B1 (دوبار refund)، B2 (pendingAmount)
  // ============================================================
  async deleteCourse(courseId) {
    const data = await this._fetchData(true);
    const course = data.courses.find((c) => c.id === courseId);
    if (!course) return false;

    // ✅ B1: فقط اگه قبلاً refund نشده، refund کن
    const alreadyRefunded =
      course.refundProcessed === true ||
      course.status === COURSE_STATUS.CANCELLED;

    if (!alreadyRefunded && course.enrolledStudents?.length) {
      // کاهش pendingAmount معلم (خالص)
      const teacherAmount = computeCourseNetPending(course, data.adminSettings);
      if (!course.pendingReleased) {
        this._decreaseTeacherPendingInData(data, course.teacherId, teacherAmount);
      }

      // refund کامل (پایه + VAT) به همه‌ی دانش‌آموزان
      for (const student of course.enrolledStudents) {
        const u = data.users.find((x) => x.id === student.userId);
        if (u) {
          const fullRefund = typeof student.totalPaid === 'number'
            ? student.totalPaid
            : Math.round((course.price || 0) * (1 + VAT_RATE / 100));
          u.wallet = (u.wallet || 0) + fullRefund;
          if (u.enrolledCourses) {
            u.enrolledCourses = u.enrolledCourses.filter((id) => id !== courseId);
          }
          data.transactions.push({
            id: Date.now() + Math.random(),
            userId: u.id,
            amount: fullRefund,
            date: moment().format('jYYYY-jMM-jDD'),
            type: 'refund',
            courseId: course.id,
            courseTitle: course.title,
            note: 'حذف کلاس — بازگشت کامل (شامل مالیات)',
          });
        }
      }
    } else if (course.enrolledStudents?.length) {
      // فقط پاک کردن enrolledCourses (بدون refund چون قبلاً انجام شده)
      for (const student of course.enrolledStudents) {
        const u = data.users.find((x) => x.id === student.userId);
        if (u?.enrolledCourses) {
          u.enrolledCourses = u.enrolledCourses.filter((id) => id !== courseId);
        }
      }
    }

    // حذف از آرایه
    data.courses = data.courses.filter((c) => c.id !== courseId);
    await this._saveData(data);

    // حذف اتاق از Roomeet
    if (course.siroomehClassId) {
      try {
        const adminApi = await getAdminRoomeetApi();
        if (adminApi) {
          try { await adminApi.endRoom(course.siroomehClassId); } catch (_) {}
          await adminApi.removeRoom(course.siroomehClassId);
        }
      } catch (e) {
        console.warn('[deleteCourse→Roomeet]', e.message);
      }
    }

    return true;
  }

  async hideCourseForUser(courseId, userId) {
    const data = await this._fetchData(true);
    const course = data.courses.find((c) => c.id === courseId);
    if (!course) return false;
    if (!course.hiddenByUsers) course.hiddenByUsers = [];
    if (!course.hiddenByUsers.includes(userId)) {
      course.hiddenByUsers.push(userId);
    }
    await this._saveData(data);
    return true;
  }

  async sendBulkNotification(message, type = 'info', roleFilter = null) {
    const data = await this._fetchData(true);
    const users = data.users.filter((u) => !roleFilter || u.role === roleFilter);
    const notifications = users.map((u) => ({
      id: Date.now() + Math.random() * 1000, message, type, read: false,
      date: moment().format('jYYYY-jMM-jDD'), userId: u.id,
    }));
    data.notifications = [...notifications, ...data.notifications];
    await this._saveData(data);
    return notifications;
  }

  // ============================================================
  // ✅ cancelCourse — حل A11 (zero enrolled)، B2 (pending)، B1 (refundProcessed)
  // ============================================================
  async cancelCourse(courseId, reason = '') {
    const data = await this._fetchData(true);
    const idx = data.courses.findIndex((c) => c.id === courseId);
    if (idx === -1) return false;
    const course = data.courses[idx];
    if (course.status === COURSE_STATUS.CANCELLED) return false;

    // پایان اتاق در Roomeet (اگه live باشه)
    if (course.siroomehClassId && course.status === COURSE_STATUS.LIVE) {
      try {
        const adminApi = await getAdminRoomeetApi();
        if (adminApi) await adminApi.endRoom(course.siroomehClassId);
      } catch (_) {}
    }

    // ✅ B2: کاهش pendingAmount معلم (فقط خالص، هماهنگ با enrollStudent)
    const teacherAmount = computeCourseNetPending(course, data.adminSettings);
    if (!course.pendingReleased && teacherAmount > 0) {
      this._decreaseTeacherPendingInData(data, course.teacherId, teacherAmount);
    }

    // refund کامل (پایه + VAT) — انگار ثبت‌نام نکرده
    if (course.enrolledStudents?.length) {
      for (const student of course.enrolledStudents) {
        const user = data.users.find((u) => u.id === student.userId);
        if (user) {
          const fullRefund = typeof student.totalPaid === 'number'
            ? student.totalPaid
            : Math.round((course.price || 0) * (1 + VAT_RATE / 100));
          user.wallet = (user.wallet || 0) + fullRefund;
          if (user.enrolledCourses) {
            user.enrolledCourses = user.enrolledCourses.filter((id) => id !== courseId);
          }
          data.transactions.push({
            id: Date.now() + Math.random(), userId: user.id, amount: fullRefund,
            date: moment().format('jYYYY-jMM-jDD'), type: 'refund',
            courseId: course.id, courseTitle: course.title,
            note: 'لغو کلاس — بازگشت کامل (شامل مالیات)',
          });
        }
      }
    }

    data.courses[idx] = {
      ...course,
      status: COURSE_STATUS.CANCELLED,
      cancelledAt: moment().format('jYYYY-jMM-jDD HH:mm'),
      cancelReason: reason,
      roomIsLive: false,
      roomOfflineSince: null,
      refundProcessed: true,
      // ✅ A11: zero کردن
      enrolled: 0,
      enrolledStudents: [],
    };
    await this._saveData(data);
    return true;
  }

  // ============================================================
  // releasePendingPayment — آزادسازی وجه معلم
  // ============================================================
  async releasePendingPayment(courseId) {
    try {
      const token = getToken();
      const res = await fetch('/api/actions/release-payment', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ courseId })
      });
      
      const result = await res.json();
      if (!res.ok) {
        throw new Error(result.error || 'خطا در تسویه حساب');
      }
      return true;
    } catch (e) {
      console.warn('releasePendingPayment error:', e);
      return false;
    }
  }
}

// ================================================================
// ۷. Data Provider — با حلقه‌ی Sync واحد
// ================================================================
export function DataProvider({ children }) {
  const [state, dispatch] = useReducer(dataReducer, initialDataState);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const roomeetApi = useMemo(() => new RoomeetAPIService(), []);
  const internalApi = useMemo(
    () => new InternalAPIService(roomeetApi),
    [roomeetApi]
  );

  const refreshData = useCallback(async () => {
    setLoading(true);
    try {
      const [courses, users, requests, transactions, wishlist, notifications, adminSettings] =
        await Promise.all([
          internalApi.getCourses(),
          internalApi.getUsers(),
          internalApi.getRequests(),
          internalApi.getTransactions(),
          internalApi.getWishlist(),
          internalApi.getNotifications(),
          internalApi.getAdminSettings(),
        ]);
      dispatch({
        type: 'SET_DATA',
        payload: {
          courses, users, requests, transactions, wishlist, notifications,
          adminSettings, error: null,
        },
      });
      setError(null);
    } catch (e) {
      setError(e.message);
      dispatch({ type: 'SET_ERROR', payload: e.message });
    } finally {
      setLoading(false);
    }
  }, [internalApi]);

  useEffect(() => { refreshData(); }, [refreshData]);

  // Backfill اتوماتیک
  const backfillInFlight = useRef(false);

  useEffect(() => {
    if (loading) return undefined;
    if (!state.courses?.length) return undefined;
    if (backfillInFlight.current) return undefined;

    const BACKFILL_KEY = 'roomeet_backfill_ts_v5';
    const lastRun = Number(localStorage.getItem(BACKFILL_KEY) || 0);
    const THIRTY_MIN = 30 * 60 * 1000;
    if (Date.now() - lastRun < THIRTY_MIN) return undefined;

    let cancelled = false;
    backfillInFlight.current = true;

    const run = async () => {
      try {
        const hasPendingUsers = state.users.some(
          (u) => !u.roomeetMemberId && u.phone && u.role !== 'admin'
        );

        if (hasPendingUsers) {
          await internalApi.syncAllUsersToRoomeet();
        }

        const roomResult = await internalApi.syncAllRoomMemberships();
        if (roomResult.added > 0 && !cancelled) refreshData();

        if (!cancelled) {
          localStorage.setItem(BACKFILL_KEY, String(Date.now()));
        }
      } catch (e) {
        console.warn('[Backfill] خطا:', e.message);
      } finally {
        backfillInFlight.current = false;
      }
    };

    const t = setTimeout(run, 5000);
    return () => { cancelled = true; clearTimeout(t); };
  }, [loading, state.courses?.length, state.users, internalApi, refreshData]);

  // ✅ حلقه‌ی Sync یکپارچه — silent refresh + syncRooms
// هر ۸ ثانیه: همه‌ی داده‌ها از سرور دوباره لود می‌شن (بدون loading state)
useEffect(() => {
  if (loading) return undefined;
  let cancelled = false;

  const tick = async () => {
    if (cancelled) return;

    // ─── مرحله ۱: Roomeet-specific logic ───
    // (live detection, auto-end timeout, duration sync)
    try {
      await internalApi.syncRooms(); // بدون onChange — فقط DB رو آپدیت می‌کنه
    } catch (e) {
      console.warn('[SyncRooms] خطا:', e.message);
    }

    if (cancelled) return;

    // ─── مرحله ۲: silent refresh کامل ───
    // همه‌ی داده‌ها از سرور خونده می‌شه و یک‌جا dispatch می‌شه
    try {
      const data = await internalApi._fetchData(true);
      if (cancelled) return;
      dispatch({
        type: 'SET_DATA',
        payload: {
          courses: data.courses,
          users: data.users,
          requests: data.requests,
          transactions: data.transactions,
          wishlist: data.wishlist,
          notifications: data.notifications,
          adminSettings: data.adminSettings,
        },
      });
    } catch (e) {
      console.warn('[SilentRefresh] خطا:', e.message);
    }
  };

  const t = setTimeout(tick, 3000);
  const interval = setInterval(tick, 8000);
  return () => {
    cancelled = true;
    clearTimeout(t);
    clearInterval(interval);
  };
}, [loading, internalApi]);






  const value = useMemo(
    () => ({ state, dispatch, refreshData, internalApi, roomeetApi, loading, error }),
    [state, refreshData, internalApi, roomeetApi, loading, error]
  );
  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}
DataProvider.propTypes = { children: PropTypes.node };

// ================================================================
// ۸. متغیرهای سراسری — همگی از curriculum.js استخراج می‌شوند
// ================================================================

// ✅ سازگاری با API قبلی: آرایه‌ی عنوان کوتاه پایه‌ها (+ همه پایه‌ها)
export const gradeOptions = [
  'همه پایه‌ها',
  ...GRADE_LIST.map((g) => g.shortTitle),
];

// ✅ سازگاری با API قبلی: رشته‌ها با shortTitle
export const fieldOptions = FIELD_LIST.map((f) => f.shortTitle);

// ✅ نقشه‌ی دروس — از curriculum ساخته می‌شود
//    کلید بدون رشته: shortTitle پایه  →  ['ریاضی', 'فارسی', ...]
//    کلید با رشته:   `${shortTitle}_${shortTitle}`
export const subjectMap = (() => {
  const map = {};
  for (const g of GRADE_LIST) {
    if (g.hasFields) {
      for (const f of FIELD_LIST) {
        map[`${g.shortTitle}_${f.shortTitle}`] = getSubjectsFor(g.id, f.id).map(
          (s) => s.shortTitle
        );
      }
    } else {
      map[g.shortTitle] = getSubjectsFor(g.id).map((s) => s.shortTitle);
    }
  }
  return map;
})();

// ✅ سازگاری کامل با signature قبلی — (gradeTitle, fieldTitle)
export const getSubjects = (grade, field) => {
  if (!grade || grade === 'همه پایه‌ها') return [];
  const gradeId = normalizeGradeId(grade);
  if (!gradeId) return [];
  const g = getGrade(gradeId);
  if (!g) return [];

  if (g.hasFields) {
    if (!field) return [];
    const fieldId = normalizeFieldId(field);
    if (!fieldId) return [];
    return getSubjectsFor(gradeId, fieldId).map((s) => s.shortTitle);
  }
  return getSubjectsFor(gradeId).map((s) => s.shortTitle);
};

export const capacityOptions = [1,2,3,4,5,6,7,8,9,10,15,20,25,30,40,50];

// ================================================================
// ۹. توابع کمکی
// ================================================================
export const getJalaliDaysInMonth = (year, month) => {
  let m;
  try { m = moment(`${year}/${String(month).padStart(2,'0')}/01`, 'jYYYY/jMM/jDD'); } catch (_) { m = null; }
  if (m && m.isValid && m.isValid()) {
    if (typeof m.jDaysInMonth === 'function') {
      try { const r = m.jDaysInMonth(); if (r >= 29 && r <= 31) return r; } catch (_) {}
    }
    if (typeof m.daysInMonth === 'function') {
      try { const r = m.daysInMonth(); if (r >= 29 && r <= 31) return r; } catch (_) {}
    }
  }
  if (typeof moment.jDaysInMonth === 'function') {
    try { const r = moment.jDaysInMonth(year, month); if (r >= 29 && r <= 31) return r; } catch (_) {}
  }
  if (month <= 6) return 31;
  if (month <= 11) return 30;
  if (typeof moment.jIsLeapYear === 'function') {
    try { return moment.jIsLeapYear(year) ? 30 : 29; } catch (_) {}
  }
  const breaks = [-61,9,38,199,426,686,756,818,1111,1181,1210,1635,2060,2097,2192,2262,2324,2394,2456,3178];
  let jp = breaks[0], jump = 0;
  for (let i = 1; i < breaks.length; i++) {
    const jm = breaks[i]; jump = jm - jp;
    if (year < jm) break;
    jp = jm;
  }
  let n = year - jp;
  if (jump - n < 6) n = n - jump + Math.floor((jump + 4) / 33) * 33;
  let leap = ((n + 1) % 33 - 1) % 4;
  if (leap === -1) leap = 4;
  return leap === 0 ? 30 : 29;
};

// ✅ تقویت‌شده: String + شامل _id، حذف whitespace
export const getRoomId = (room) => {
  if (!room) return '';
  const id =
    room.theIdOfMeeting ||
    room.meetingID ||
    room.meetingId ||
    room._id ||
    room.id ||
    '';
  return String(id).trim();
};

export const extractOnlineRoomIds = (onlineRes) => {
  const onlineIds = new Set();
  (onlineRes?.onlineMeetings || []).forEach((r) => {
    [r.theIdOfMeeting, r.meetingID, r.meetingId, r.id,
     r.metadata?.theIdOfMeeting, r.metadata?.meetingId]
      .filter(Boolean).forEach((id) => onlineIds.add(String(id)));
  });
  return onlineIds;
};

export const getCourseStartMoment = (course) => {
  if (!course?.date || !course?.time) return null;
  try {
    return moment(course.date + ' ' + course.time, 'jYYYY-jMM-jDD HH:mm');
  } catch (_) {
    return null;
  }
};

export const canStartCourse = (course, nowMoment = moment()) => {
  if (!course?.siroomehClassId) return false;
  if (course.status !== COURSE_STATUS.SCHEDULED) return false;
  const startMoment = getCourseStartMoment(course);
  if (!startMoment) return false;
  const diffSec = nowMoment.diff(startMoment, 'seconds');
  return diffSec >= -300 && diffSec <= 3 * 3600;
};

export const getSecondsUntilStart = (course, nowMoment = moment()) => {
  const startMoment = getCourseStartMoment(course);
  if (!startMoment) return null;
  return startMoment.diff(nowMoment, 'seconds');
};

export const getCourseStatusLabel = (course, nowMoment = moment()) => {
  if (!course) return '';
  switch (course.status) {
    case COURSE_STATUS.CANCELLED:
      return '❌ لغو شده';
    case COURSE_STATUS.ENDED:
      return '✅ کلاس تمام شد';
    case COURSE_STATUS.LIVE:
      return '🟢 در حال برگزاری';
    case COURSE_STATUS.SCHEDULED: {
      const sec = getSecondsUntilStart(course, nowMoment);
      if (sec === null) return '⏳ در انتظار';
      if (sec < -300) return '⏳ در انتظار شروع';
      if (sec <= 0) return '🎬 وقت شروع کلاس';
      if (sec < 60) return `⏳ ${sec} ثانیه تا شروع`;
      if (sec < 3600) return `⏳ ${Math.floor(sec / 60)} دقیقه تا شروع`;
      if (sec < 86400) return `⏳ ${Math.floor(sec / 3600)} ساعت تا شروع`;
      return `⏳ ${Math.floor(sec / 86400)} روز تا شروع`;
    }
    default:
      return '';
  }
};

// ================================================================
// ۱۰. کامپوننت‌های کمکی
// ================================================================
export const PersianCalendar = memo(({ courses = [], onDateClick }) => {
  const [year, setYear] = useState(moment().jYear());
  const [month, setMonth] = useState(moment().jMonth() + 1);
  const monthNames = ['فروردین','اردیبهشت','خرداد','تیر','مرداد','شهریور','مهر','آبان','آذر','دی','بهمن','اسفند'];

  const days = useMemo(() => {
    const daysInMonth = getJalaliDaysInMonth(year, month);
    let firstDay = 0;
    try {
      const first = moment(`${year}-${String(month).padStart(2,'0')}-01`, 'jYYYY-jMM-jDD');
      if (first && typeof first.jDay === 'function') firstDay = first.jDay();
      else if (first && typeof first.day === 'function') firstDay = (first.day() + 1) % 7;
    } catch (_) { firstDay = 0; }

    const result = [];
    for (let i = 0; i < firstDay; i++) result.push({ empty: true });
    const today = moment().format('jYYYY-jMM-jDD');
    for (let i = 1; i <= daysInMonth; i++) {
      const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(i).padStart(2,'0')}`;
      result.push({
        day: i, date: dateStr, isToday: dateStr === today,
        hasClass: courses.some((c) => c.date === dateStr && c.status !== COURSE_STATUS.CANCELLED),
      });
    }
    return result;
  }, [year, month, courses]);

  const prevMonth = () => {
    if (month === 1) { setYear(year - 1); setMonth(12); } else setMonth(month - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setYear(year + 1); setMonth(1); } else setMonth(month + 1);
  };

  return (
    <div className="calendar-widget">
      <div className="calendar-nav">
        <button onClick={prevMonth} aria-label="ماه قبل"><i className="fas fa-chevron-right" /></button>
        <span>{monthNames[month - 1]} {year}</span>
        <button onClick={nextMonth} aria-label="ماه بعد"><i className="fas fa-chevron-left" /></button>
      </div>
      <div className="calendar-grid">
        {['ش','ی','د','س','چ','پ','ج'].map((d) => (
          <div key={d} className="cal-header">{d}</div>
        ))}
        {days.map((d, idx) => (
          <div
            key={idx}
            className={`cal-day ${d.isToday ? 'today' : ''} ${d.hasClass ? 'has-class' : ''} ${d.empty ? 'empty' : ''}`}
            onClick={() => !d.empty && onDateClick && onDateClick(d.date)}
            role="button"
            tabIndex={d.empty ? -1 : 0}
          >
            {!d.empty && (<>{d.day}{d.hasClass && <span className="cal-badge">●</span>}</>)}
          </div>
        ))}
      </div>
      <div className="cal-legend">
        <span><span className="dot today" /> امروز</span>
        <span><span className="dot has-class" /> کلاس</span>
      </div>
    </div>
  );
});
PersianCalendar.displayName = 'PersianCalendar';
PersianCalendar.propTypes = { courses: PropTypes.array, onDateClick: PropTypes.func };

export const Modal = memo(({ isOpen, onClose, title, children }) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleEsc = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="بستن">&times;</button>
        {title && <h2 style={{ marginBottom: '1rem', color: 'var(--pc-text-primary)' }}>{title}</h2>}
        {children}
      </div>
    </div>
  );
});
Modal.displayName = 'Modal';
Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired, onClose: PropTypes.func.isRequired,
  title: PropTypes.string, children: PropTypes.node,
};

export const RatingStars = memo(({ rating = 0, onRate, readonly = false, size = 24 }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="rating-stars" style={{ fontSize: size }}>
      {[1,2,3,4,5].map((s) => (
        <span
          key={s}
          className={`star ${(hover || rating) >= s ? 'active' : ''}`}
          onMouseEnter={() => !readonly && setHover(s)}
          onMouseLeave={() => !readonly && setHover(0)}
          onClick={() => !readonly && onRate && onRate(s)}
          role={readonly ? undefined : 'button'}
          tabIndex={readonly ? -1 : 0}
        >★</span>
      ))}
    </div>
  );
});
RatingStars.displayName = 'RatingStars';
RatingStars.propTypes = {
  rating: PropTypes.number, onRate: PropTypes.func,
  readonly: PropTypes.bool, size: PropTypes.number,
};

export const PieChart = memo(({ percent, label }) => {
  const [p, setP] = useState(0);
  useEffect(() => setP(percent), [percent]);
  return <div className="pie-chart" style={{ '--p': p + '%' }} data-label={label || `${p}%`} />;
});
PieChart.displayName = 'PieChart';
PieChart.propTypes = { percent: PropTypes.number.isRequired, label: PropTypes.string };

export const Confetti = memo(({ count = 50, duration = 3000 }) => {
  const [pieces, setPieces] = useState([]);
  useEffect(() => {
    const colors = ['#fbbf24','#ef4444','#22c55e','#3b82f6','#8b5cf6','#ec4899','#f97316'];
    setPieces(Array.from({ length: count }, (_, i) => ({
      id: i, x: Math.random() * 100, y: -10 - Math.random() * 20,
      size: 6 + Math.random() * 8,
      color: colors[Math.floor(Math.random() * colors.length)],
      rotate: Math.random() * 360, delay: Math.random() * 0.5,
      duration: 2 + Math.random() * 2,
    })));
    const timer = setTimeout(() => setPieces([]), duration);
    return () => clearTimeout(timer);
  }, [count, duration]);

  if (pieces.length === 0) return null;
  return (
    <div className="confetti-container" style={{ opacity: 1, transition: 'opacity 0.5s' }}>
      {pieces.map((p) => (
        <div
          key={p.id} className="confetti-piece"
          style={{
            left: p.x + '%', top: p.y + '%', width: p.size + 'px', height: p.size + 'px',
            background: p.color, transform: `rotate(${p.rotate}deg)`,
            animationDuration: p.duration + 's', animationDelay: p.delay + 's',
          }}
        />
      ))}
    </div>
  );
});
Confetti.displayName = 'Confetti';
Confetti.propTypes = { count: PropTypes.number, duration: PropTypes.number };

export const PersianDatePicker = memo(({ value, onChange }) => {
  const currentYear = moment().jYear();
  const currentMonth = moment().jMonth() + 1;
  const currentDay = moment().jDate();

  let selectedYear = currentYear, selectedMonth = currentMonth, selectedDay = currentDay;
  if (value && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const parts = value.split('-');
    selectedYear = parseInt(parts[0], 10);
    selectedMonth = parseInt(parts[1], 10);
    selectedDay = parseInt(parts[2], 10);
  }

  const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
  const monthNames = ['فروردین','اردیبهشت','خرداد','تیر','مرداد','شهریور','مهر','آبان','آذر','دی','بهمن','اسفند'];
  const daysInMonth = getJalaliDaysInMonth(selectedYear, selectedMonth);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const handleYearChange = (e) => {
    const newYear = parseInt(e.target.value, 10);
    onChange(`${newYear}-${String(selectedMonth).padStart(2,'0')}-${String(selectedDay).padStart(2,'0')}`);
  };
  const handleMonthChange = (e) => {
    const newMonth = parseInt(e.target.value, 10);
    const newDaysInMonth = getJalaliDaysInMonth(selectedYear, newMonth);
    let newDay = selectedDay;
    if (newDay > newDaysInMonth) newDay = newDaysInMonth;
    onChange(`${selectedYear}-${String(newMonth).padStart(2,'0')}-${String(newDay).padStart(2,'0')}`);
  };
  const handleDayChange = (e) => {
    const newDay = parseInt(e.target.value, 10);
    onChange(`${selectedYear}-${String(selectedMonth).padStart(2,'0')}-${String(newDay).padStart(2,'0')}`);
  };

  return (
    <div className="pc-date-selector">
      <select value={selectedYear} onChange={handleYearChange}>
        {years.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
      <select value={selectedMonth} onChange={handleMonthChange}>
        {monthNames.map((name, idx) => (<option key={idx + 1} value={idx + 1}>{name}</option>))}
      </select>
      <select value={selectedDay} onChange={handleDayChange}>
        {days.map((d) => <option key={d} value={d}>{d}</option>)}
      </select>
    </div>
  );
});
PersianDatePicker.displayName = 'PersianDatePicker';
PersianDatePicker.propTypes = { value: PropTypes.string, onChange: PropTypes.func.isRequired };

// ================================================================
// ۱۱. ثابت‌های فرم‌ها
// ================================================================
export const DEFAULT_ROOM_FORM = {
  name: '', guest: true, welcomeMessage: '', moderatorOnlyMessage: '',
  startWithModerator: false, duration: 60, language: 'fa',
  allowDownloadRecordings: true, webcamsOnlyForModerat: true,
  autoStartRecording: false, muteOnStart: false,
  allowModsToEjectCameras: true, disablePresentation: false,
  logoutURL: '', lockSettingsDisablePrivateChat: false,
  lockSettingsDisablePublicChat: false, lockSettingsHideUserList: false,
  endWhenNoModeratorDelayInMinutes: 15,
  teacherId: '', date: '', time: '', price: 150000, capacity: 5,
};

export const DEFAULT_EDIT_ROOM_FORM = {
  name: '', guest: true, welcomeMessage: '', moderatorOnlyMessage: '',
  startWithModerator: false, duration: 60, language: 'fa',
  allowDownloadRecordings: true, webcamsOnlyForModerat: true,
  autoStartRecording: false, muteOnStart: false,
  allowModsToEjectCameras: true, disablePresentation: false,
  logoutURL: '', lockSettingsDisablePrivateChat: false,
  lockSettingsDisablePublicChat: false, lockSettingsHideUserList: false,
  endWhenNoModeratorDelayInMinutes: 15, saleId: '', date: '', time: '',
};

export const DEFAULT_TEACHER_REQUEST_FORM = {
  // ✅ مقادیر display-based برای سازگاری با UI فعلی
  grade: 'دهم', field: 'ریاضی', subject: '', title: '', description: '',
  date: '', time: '', duration: 60, price: 150000, capacity: 2,
};

export const DEFAULT_NEW_USER = {
  name: '', phone: '', password: '', role: 'student', wallet: 0,
};

export const buildRoomPayload = (form) => ({
  name: form.name, guest: form.guest,
  welcomeMessage: form.welcomeMessage, moderatorOnlyMessage: form.moderatorOnlyMessage,
  startWithModerator: form.startWithModerator, duration: form.duration,
  language: form.language, allowDownloadRecordings: form.allowDownloadRecordings,
  webcamsOnlyForModerat: form.webcamsOnlyForModerat,
  autoStartRecording: form.autoStartRecording, muteOnStart: form.muteOnStart,
  allowModsToEjectCameras: form.allowModsToEjectCameras,
  disablePresentation: form.disablePresentation, logoutURL: form.logoutURL,
  lockSettingsDisablePrivateChat: form.lockSettingsDisablePrivateChat,
  lockSettingsDisablePublicChat: form.lockSettingsDisablePublicChat,
  lockSettingsHideUserList: form.lockSettingsHideUserList,
  endWhenNoModeratorDelayInMinutes: form.endWhenNoModeratorDelayInMinutes,
});

// ================================================================
// ۱۳. هوک همگام‌سازی کلاس‌های زنده (خواندن از state)
// ================================================================
export function useLiveRoomSync({ onOnlineIdsChange } = {}) {
  const { state } = useData();
  const onIdsRef = useRef(onOnlineIdsChange);
  useEffect(() => { onIdsRef.current = onOnlineIdsChange; }, [onOnlineIdsChange]);

  useEffect(() => {
    const ids = new Set();
    for (const c of state?.courses || []) {
      if (c.roomIsLive && c.siroomehClassId) ids.add(String(c.siroomehClassId));
    }
    onIdsRef.current?.(ids);
  }, [state?.courses]);
}

// ================================================================
// ۱۴. هوک اسکرول
// ================================================================
export function useScrollProgress() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(docHeight > 0 ? (scrollTop / docHeight) * 100 : 0);
      setHeaderScrolled(scrollTop > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return { scrollProgress, headerScrolled };
}

// ================================================================
// ۱۵. هوک جستجوی ⌘K
// ================================================================
export function useSearchShortcut(inputRef) {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [inputRef]);
}

// ================================================================
// ۱۶. PageParticles
// ================================================================
export const PageParticles = memo(() => (
  <div className="bg-particles">
    {[...Array(20)].map((_, i) => (
      <span
        key={i} className="bg-particle"
        style={{
          '--i': i, '--size': 5 + (i % 8) + 'px',
          '--x': ((i * 7) % 100) + '%', '--y': ((i * 11) % 100) + '%',
        }}
      />
    ))}
  </div>
));
PageParticles.displayName = 'PageParticles';

// ================================================================
// ۱۷. احراز هویت Roomeet — با ✅ E26: چک حذف کاربر
// ================================================================
export function useRoomeetAuthGate({ user, updateUser, roomeetApi, requiredRole, users }) {
  const [status, setStatus] = useState('checking');
  const [loginError, setLoginError] = useState('');
  const loginSuccessRef = useRef(false);
  const lastUserIdRef = useRef(null);
  const isMountedRef = useRef(true);
  const updateUserRef = useRef(updateUser);
  const userRef = useRef(user);
  updateUserRef.current = updateUser;
  userRef.current = user;

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // ✅ E26: چک حذف‌شدن کاربر توسط ادمین
    // ✅ E26: چک حذف‌شدن کاربر توسط ادمین (بدون false-positive)
  useEffect(() => {
    if (!users || !user) return;
    // ✅ CRITICAL: تا وقتی users لود نشده، این چک را انجام نده
    // وگرنه کاربر fake-positive می‌شود و force logout می‌شود
    if (users.length === 0) return;
    if (status !== 'ready') return;
    if (!loginSuccessRef.current) return;

    const stillExists = users.some((u) => u.id === user.id);
    if (!stillExists) {
      roomeetApi.clearAuth();
      clearRoomeetCreds();
      loginSuccessRef.current = false;
      setStatus('needs-login');
      setLoginError('حساب کاربری شما توسط ادمین حذف شده است.');
    }
  }, [users, user?.id, status, roomeetApi]);

  useEffect(() => {
    if (!roomeetApi) return undefined;
    const prev = roomeetApi.onAuthError;
    roomeetApi.onAuthError = () => {
      if (!isMountedRef.current) return;
      loginSuccessRef.current = false;
      setStatus('needs-login');
      setLoginError('اتصال به Roomeet منقضی شده. لطفاً دوباره وارد شوید.');
    };
    return () => { roomeetApi.onAuthError = prev; };
  }, [roomeetApi]);

  useEffect(() => {
    let cancelled = false;
    let timeoutHandle = null;
    const currentUser = userRef.current;

    if (!currentUser) { setStatus('checking'); return undefined; }

    if (currentUser.id !== lastUserIdRef.current) {
      lastUserIdRef.current = currentUser.id;
      loginSuccessRef.current = false;
      try {
        const raw = localStorage.getItem(ROOMEET_AUTH_KEY);
        if (raw) {
          const stored = JSON.parse(raw);
          if (stored && stored.userId && stored.userId !== currentUser.id) {
            roomeetApi.clearAuth();
          }
        }
      } catch (_) {}
    }

    if (requiredRole && currentUser.role !== requiredRole) {
      setStatus('ready'); return undefined;
    }
    if (loginSuccessRef.current && roomeetApi.isAuthenticated()) {
      setStatus('ready'); return undefined;
    }
    if (roomeetApi.isAuthenticated()) {
      loginSuccessRef.current = true;
      setStatus('ready'); return undefined;
    }

    const safeSetStatus = (s) => {
      if (!cancelled && isMountedRef.current) setStatus(s);
    };

    const establish = async () => {
      let token = currentUser.roomeetToken;
      let sid = currentUser.schoolId;
      let role = currentUser.roomeetRole;
      if (!token || !sid) {
        try {
          const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
          token = token || storedUser.roomeetToken;
          sid = sid || storedUser.schoolId;
          role = role || storedUser.roomeetRole;
        } catch (_) {}
      }

      if (token && sid) {
        roomeetApi.setAuth(token, sid, currentUser.id, role);
        if (roomeetApi.isAuthenticated()) {
          loginSuccessRef.current = true;
          safeSetStatus('ready');
          return;
        }
        roomeetApi.clearAuth();
      }

      const creds = getRoomeetCreds();
      if (creds?.phone && creds?.password) {
        try {
          const result = await roomeetApi.login(creds.phone, creds.password);
          if (cancelled || !isMountedRef.current) return;
          if (result?.token && result?.schools?.length) {
            const school = result.schools[0] || {};
            const schoolId = extractSchoolId(school);
            const role2 = extractRole(school);
            roomeetApi.setAuth(result.token, schoolId, currentUser.id, role2);
            loginSuccessRef.current = true;

            const needsUpdate =
              currentUser.roomeetToken !== result.token ||
              currentUser.schoolId !== schoolId ||
              currentUser.roomeetRole !== role2;

            if (needsUpdate && typeof updateUserRef.current === 'function') {
              updateUserRef.current({
                ...currentUser, roomeetToken: result.token, schoolId,
                roomeetRole: role2, roomeetPhone: creds.phone,
              });
            }
            safeSetStatus('ready');
            return;
          }
        } catch (err) {
          if (!cancelled && isMountedRef.current) {
            setLoginError(err.message || 'ورود خودکار ناموفق بود');
          }
        }
      }

      safeSetStatus('needs-login');
    };

    setStatus('checking');
    timeoutHandle = setTimeout(() => {
      if (!cancelled && isMountedRef.current && !loginSuccessRef.current) {
        setStatus('needs-login');
        setLoginError('اتصال به Roomeet طولانی شد. لطفاً دوباره تلاش کنید.');
      }
    }, 10000);

    establish().finally(() => { if (timeoutHandle) clearTimeout(timeoutHandle); });

    return () => { cancelled = true; if (timeoutHandle) clearTimeout(timeoutHandle); };
  }, [user?.id, requiredRole, roomeetApi]);

  const login = useCallback(async (phone, password) => {
    setLoginError('');
    try {
      const result = await roomeetApi.login(phone, password);
      if (!result?.token || !result?.schools?.length) {
        throw new Error('پاسخ سرور معتبر نیست.');
      }
      const school = result.schools[0] || {};
      const schoolId = extractSchoolId(school);
      const role = extractRole(school);
      roomeetApi.setAuth(result.token, schoolId, userRef.current?.id, role);
      saveRoomeetCreds(phone, password);
      loginSuccessRef.current = true;
      if (userRef.current?.id && typeof updateUserRef.current === 'function') {
        updateUserRef.current({
          ...userRef.current, roomeetToken: result.token, schoolId,
          roomeetRole: role, roomeetPhone: phone,
        });
      }
      setStatus('ready');
      return { success: true };
    } catch (err) {
      const msg = err.message || 'ورود به Roomeet ناموفق بود';
      setLoginError(msg);
      return { success: false, error: msg };
    }
  }, [roomeetApi]);

  const logout = useCallback(() => {
    roomeetApi.clearAuth();
    clearRoomeetCreds();
    loginSuccessRef.current = false;
    setStatus('needs-login');
  }, [roomeetApi]);

  return {
    status,
    isReady: status === 'ready',
    isChecking: status === 'checking',
    needsLogin: status === 'needs-login',
    loginError, login, logout,
  };
}

// ----------------------------------------------------------------
export function RoomeetCheckingScreen({ roleLabel = 'کاربر' }) {
  return (
    <div className="private-class-page">
      <PageParticles />
      <div className="pc-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <div className="card glass" style={{ maxWidth: '500px', width: '100%', padding: '3rem 2rem', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔄</div>
          <h2 style={{ color: 'var(--pc-text-primary)', marginBottom: '0.5rem' }}>در حال اتصال به سرور کلاس‌ها...</h2>
          <p style={{ color: 'var(--pc-text-muted)' }}>اتصال {roleLabel} به Roomeet در حال بررسی است</p>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------
export function RoomeetLoginScreen({ onSubmit, error, roleLabel = 'کاربر', roleIcon = 'fa-user', hint = '' }) {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!phone || !password) return;
    setIsLoading(true);
    try { await onSubmit(phone, password); } finally { setIsLoading(false); }
  };

  return (
    <div className="private-class-page">
      <PageParticles />
      <div className="pc-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <div className="card glass" style={{ maxWidth: '550px', width: '100%', padding: '2.5rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🔑</div>
            <h2 style={{ color: 'var(--pc-text-primary)' }}>اتصال به Roomeet</h2>
            <p style={{ color: 'var(--pc-text-muted)' }}>
              برای ورود به بخش {roleLabel}، اطلاعات حساب Roomeet خود را وارد کنید.
            </p>
            {error && (
              <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: 'rgba(244,63,94,0.1)', borderRadius: '8px', color: '#f43f5e', fontSize: '0.9rem' }}>
                ⚠️ {error}
              </div>
            )}
          </div>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label><i className="fas fa-phone" /> شماره تلفن Roomeet</label>
              <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="مثال: 09123456789" required dir="ltr" />
            </div>
            <div className="form-group">
              <label><i className="fas fa-lock" /> رمز عبور Roomeet</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="رمز عبور حساب Roomeet" required dir="ltr" />
            </div>
            <div className="form-actions" style={{ justifyContent: 'center' }}>
              <button type="submit" className="btn-primary" disabled={isLoading} style={{ width: '100%', justifyContent: 'center' }}>
                <i className="fas fa-sign-in-alt" /> {isLoading ? 'در حال ورود...' : 'ورود به Roomeet'}
              </button>
            </div>
            {hint && (
              <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                <small style={{ color: 'var(--pc-text-muted)' }}>{hint}</small>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------
export function AuthGate({ status, roleLabel, roleIcon, requiredRole, loginError, onLogin, children }) {
  if (status === 'checking') return <RoomeetCheckingScreen roleLabel={roleLabel} />;
  if (status === 'needs-login') {
    return (
      <RoomeetLoginScreen
        onSubmit={onLogin} error={loginError}
        roleLabel={roleLabel} roleIcon={roleIcon}
        hint={`⚠️ نقش شما در Roomeet باید "${requiredRole}" باشد.`}
      />
    );
  }
  return children;
}





// ================================================================
// 🆕 مودال شارژ کیف پول (ریال و تومان)
// ================================================================


// ================================================================
// 🆕 مودال برداشت وجه (با اعتبارسنجی قرمز)
// ================================================================
// ================================================================
// 🆕 مودال برداشت وجه — با اطلاعات بانکی کامل و اعتبارسنجی حرفه‌ای
// ================================================================
// ================================================================
// 🆕 مودال برداشت وجه — استایل حرفه‌ای و مدرن
// ================================================================
// ================================================================
// 🆕 مودال شارژ کیف پول (ریال به تومان با تبدیل لحظه‌ای)


// ================================================================
// 🆕 مودال برداشت وجه — استایل حرفه‌ای و مدرن
// ================================================================
// ================================================================
// 🆕 مودال شارژ کیف پول (ریال به تومان با تبدیل لحظه‌ای)
// ================================================================
export function WalletChargeModal({ isOpen, onClose, onCharge, walletBalanceToman }) {
  const [rialAmount, setRialAmount] = useState('');
  
  if (!isOpen) return null;

  const tomanAmount = rialAmount ? Math.floor(Number(rialAmount) / 10) : 0;
  const isValid = tomanAmount >= 10000;

  const handleSubmit = () => {
    if (!isValid) return;
    onCharge(tomanAmount);
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 9999, backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
      <div 
        className="modal-content pc-modal-scrollable" 
        onClick={(e) => e.stopPropagation()} 
        style={{ 
          maxWidth: '420px', width: '90%', padding: '2rem', borderRadius: '24px', 
          background: 'var(--pc-card-bg, #ffffff)', 
          color: 'var(--pc-text-primary, #1f2937)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', 
          maxHeight: '90vh', overflowY: 'auto', position: 'relative',

        }}
      >
        <button onClick={onClose} style={{ position: 'absolute', top: '1rem', left: '1rem', background: 'var(--pc-bg-input, #f1f5f9)', border: 'none', width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer', color: 'var(--pc-text-muted, #64748b)' }}>
          <i className="fas fa-times" />
        </button>

        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{ width: '60px', height: '60px', margin: '0 auto 1rem', background: 'linear-gradient(135deg, #eff6ff, #dbeafe)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981', fontSize: '1.5rem' }}>
            <i className="fas fa-wallet" />
          </div>
          <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '800' }}>شارژ کیف پول</h3>
          <p style={{ margin: '0.5rem 0 0', color: 'var(--pc-text-muted, #64748b)', fontSize: '0.85rem' }}>
            موجودی فعلی: <strong style={{ color: 'var(--pc-text-primary, #1f2937)' }}>{(walletBalanceToman || 0).toLocaleString('fa-IR')}</strong> تومان
          </p>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '700', fontSize: '0.85rem', color: 'var(--pc-text-primary, #334155)' }}>مبلغ مورد نظر (به ریال)</label>
          <div style={{ position: 'relative' }}>
            <input
              type="number"
              value={rialAmount}
              onChange={(e) => setRialAmount(e.target.value)}
              placeholder="مثلاً 500000"
              className="pc-modal-input"
              style={{ 
                width: '100%', padding: '1rem 3.5rem 1rem 1rem', borderRadius: '12px', 
                fontSize: '1.1rem', textAlign: 'center', fontWeight: 'bold'
              }}
            />
            <span style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--pc-text-muted, #64748b)', fontSize: '0.85rem', fontWeight: 'bold', background: 'var(--pc-bg-input, #e2e8f0)', padding: '0.2rem 0.5rem', borderRadius: '6px' }}>ریال</span>
          </div>
          {rialAmount && (
            <div style={{ marginTop: '0.5rem', textAlign: 'center', fontSize: '0.9rem', color: tomanAmount >= 10000 ? 'var(--pc-success, #10b981)' : 'var(--pc-danger, #ef4444)', fontWeight: 'bold', background: tomanAmount >= 10000 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)', padding: '0.4rem', borderRadius: '8px' }}>
              معادل {tomanAmount.toLocaleString('fa-IR')} تومان
              {tomanAmount < 10000 && <div style={{ fontSize: '0.75rem', marginTop: '0.2rem' }}>حداقل مبلغ شارژ ۱۰,۰۰۰ تومان است</div>}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
          <button className="btn-outline" style={{ flex: 1, padding: '0.8rem', borderRadius: '12px', border: '2px solid var(--pc-border-color, #e2e8f0)', background: 'transparent', color: 'var(--pc-text-muted, #64748b)', fontWeight: 'bold' }} onClick={onClose}>انصراف</button>
          <button 
            className="btn-primary" 
            style={{ flex: 2, padding: '0.8rem', borderRadius: '12px', border: 'none', background: isValid ? 'linear-gradient(135deg, #10b981, #059669)' : 'var(--pc-border-color, #cbd5e1)', color: '#fff', fontWeight: 'bold', boxShadow: isValid ? '0 4px 12px rgba(16, 185, 129, 0.3)' : 'none', cursor: isValid ? 'pointer' : 'not-allowed' }} 
            onClick={handleSubmit}
            disabled={!isValid}
          >
            🔒 انتقال به درگاه
          </button>
        </div>
      </div>
    </div>
  );
}

// ================================================================
// 🆕 مودال برداشت وجه — استایل حرفه‌ای و مدرن
// ================================================================
export function WithdrawModal({ isOpen, onClose, onWithdraw, maxWithdrawToman, initialBankingInfo = {} }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [formData, setFormData] = useState({
    fullName: initialBankingInfo.fullName || '',
    cardNumber: initialBankingInfo.cardNumber || '',
    shaba: initialBankingInfo.shaba || '',
    rialAmount: '',
  });
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (isOpen) {
      setFormData({
        fullName: initialBankingInfo.fullName || '',
        cardNumber: initialBankingInfo.cardNumber || '',
        shaba: initialBankingInfo.shaba || '',
        rialAmount: '',
      });
      setTouched({});
    }
  }, [isOpen, initialBankingInfo.fullName, initialBankingInfo.cardNumber, initialBankingInfo.shaba]);

  if (!isOpen) return null;

  // ============ پالت رنگی تم‌آگاه ============
  const C = {
    // Overlay (پس‌زمینه دودی روی کل صفحه)
overlay: isDark
  ? 'radial-gradient(ellipse at top, rgba(30, 41, 59, 0.98) 0%, rgba(2, 6, 23, 0.99) 60%)'
  : 'radial-gradient(ellipse at top, #f8fafc 0%, #e2e8f0 70%, #cbd5e1 100%)',
    // Card
    cardBg: isDark
      ? 'linear-gradient(145deg, #1e293b 0%, #0f172a 100%)'
      : 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
    cardBorder: isDark ? 'rgba(148, 163, 184, 0.25)' : 'rgba(148, 163, 184, 0.2)',
    cardShadow: isDark
      ? '0 25px 60px -15px rgba(0, 0, 0, 0.9), 0 0 0 1px rgba(148, 163, 184, 0.08)'
      : '0 25px 60px -15px rgba(15, 23, 42, 0.35), 0 0 0 1px rgba(15, 23, 42, 0.04)',

    // Text
    textPrimary: isDark ? '#f1f5f9' : '#0f172a',
    textSecondary: isDark ? '#cbd5e1' : '#334155',
    textMuted: isDark ? '#94a3b8' : '#64748b',

    // Icon badge
    iconBg: isDark
      ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.25) 0%, rgba(37, 99, 235, 0.35) 100%)'
      : 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
    iconColor: isDark ? '#93c5fd' : '#3b82f6',
    iconShadow: isDark
      ? '0 8px 24px -8px rgba(59, 130, 246, 0.5)'
      : '0 8px 24px -8px rgba(59, 130, 246, 0.3)',

    // Balance badge
    balanceBg: isDark ? 'rgba(16, 185, 129, 0.15)' : 'rgba(16, 185, 129, 0.1)',
    balanceText: isDark ? '#34d399' : '#059669',

    // Inputs
    inputBg: isDark ? '#0f172a' : '#ffffff',
    inputBorder: isDark ? '#334155' : '#e2e8f0',
    inputText: isDark ? '#f1f5f9' : '#0f172a',
    inputPlaceholder: isDark ? '#64748b' : '#94a3b8',
    inputIcon: isDark ? '#64748b' : '#94a3b8',

    // Status
    danger: '#ef4444',
    dangerBg: isDark ? 'rgba(239, 68, 68, 0.15)' : 'rgba(254, 226, 226, 0.7)',
    dangerBorder: isDark ? 'rgba(239, 68, 68, 0.5)' : 'rgba(239, 68, 68, 0.4)',

    success: isDark ? '#34d399' : '#059669',
    successBg: isDark ? 'rgba(16, 185, 129, 0.15)' : 'rgba(220, 252, 231, 0.7)',

    primary: isDark ? '#60a5fa' : '#2563eb',
    primaryBg: isDark ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.08)',

    // Currency
    currencyBg: isDark ? 'rgba(148, 163, 184, 0.2)' : 'rgba(226, 232, 240, 0.9)',
    currencyText: isDark ? '#cbd5e1' : '#475569',

    // Notice
    noticeBg: isDark ? 'rgba(251, 191, 36, 0.12)' : 'rgba(254, 243, 199, 0.7)',
    noticeBorder: isDark ? 'rgba(251, 191, 36, 0.35)' : 'rgba(251, 191, 36, 0.5)',
    noticeText: isDark ? '#fcd34d' : '#92400e',
    noticeIcon: isDark ? '#fbbf24' : '#f59e0b',

    // Buttons
    outlineBorder: isDark ? '#475569' : '#cbd5e1',
    outlineText: isDark ? '#cbd5e1' : '#475569',
    primaryBtnBg: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    primaryBtnShadow: '0 10px 25px -5px rgba(37, 99, 235, 0.5)',
    disabledBg: isDark ? '#334155' : '#cbd5e1',
    disabledText: isDark ? '#64748b' : '#94a3b8',

    // Close button
    closeBg: isDark ? 'rgba(148, 163, 184, 0.15)' : 'rgba(241, 245, 249, 0.9)',
    closeText: isDark ? '#94a3b8' : '#64748b',
  };

  // ============ اعتبارسنجی ============
  const tomanAmount = formData.rialAmount ? Math.floor(Number(formData.rialAmount) / 10) : 0;
  const isOverLimit = tomanAmount > maxWithdrawToman;

  const cleanCard = formData.cardNumber.replace(/\s/g, '');
  const cleanShaba = formData.shaba.replace(/\s/g, '').toUpperCase();

  const errors = {
    fullName: !formData.fullName.trim() || formData.fullName.trim().length < 3 ? 'نام و نام خانوادگی الزامی است (حداقل ۳ حرف)' : null,
    cardNumber: !/^\d{16}$/.test(cleanCard) ? 'شماره کارت باید دقیقاً ۱۶ رقم باشد' : null,
    shaba: !/^IR\d{22}$/.test(cleanShaba) ? 'شماره شبا باید با IR شروع شود و ۲۴ کاراکتر باشد' : null,
    rialAmount: tomanAmount <= 0 ? 'مبلغ را وارد کنید' : isOverLimit ? 'مبلغ درخواستی بیشتر از موجودی است' : null,
  };

  const isValid = !Object.values(errors).some(Boolean);

  // ============ Handlers ============
  const handleCardChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 16);
    const formatted = raw.replace(/(.{4})/g, '$1 ').trim();
    setFormData((p) => ({ ...p, cardNumber: formatted }));
    setTouched((p) => ({ ...p, cardNumber: true }));
  };

  const handleShabaChange = (e) => {
    let raw = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!raw.startsWith('IR')) raw = 'IR' + raw.replace(/^IR/, '');
    raw = 'IR' + raw.slice(2, 24).replace(/\D/g, '');
    setFormData((p) => ({ ...p, shaba: raw }));
    setTouched((p) => ({ ...p, shaba: true }));
  };

  const handleSubmit = () => {
    if (!isValid) return;
    onWithdraw({
      tomanAmount,
      fullName: formData.fullName.trim(),
      cardNumber: cleanCard,
      shaba: cleanShaba,
    });
  };

  // ============ استایل‌های dynamic ============
  const inputStyle = (fieldName, hasIcon = false) => {
    const hasErr = touched[fieldName] && errors[fieldName];
    const isOk = touched[fieldName] && !errors[fieldName];
    return {
      width: '100%',
      padding: hasIcon ? '0.9rem 2.8rem 0.9rem 1rem' : '0.9rem 1rem',
      borderRadius: '14px',
      border: `1.5px solid ${hasErr ? C.danger : isOk ? C.success : C.inputBorder}`,
      fontSize: '0.95rem',
      outline: 'none',
      transition: 'all 0.2s ease',
      background: hasErr ? C.dangerBg : isOk ? C.successBg : C.inputBg,
      color: C.inputText,
      fontFamily: 'inherit',
      textAlign: fieldName === 'fullName' ? 'right' : 'center',
      fontWeight: fieldName === 'rialAmount' ? 700 : 500,
      boxShadow: hasErr
        ? '0 0 0 4px rgba(239, 68, 68, 0.1)'
        : isOk
        ? '0 0 0 4px rgba(16, 185, 129, 0.08)'
        : 'none',
    };
  };

  const renderError = (fieldName) => {
    if (!touched[fieldName] || !errors[fieldName]) return null;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.75rem', color: C.danger, marginTop: '0.4rem', fontWeight: 600 }}>
        <i className="fas fa-exclamation-circle" />
        <span>{errors[fieldName]}</span>
      </div>
    );
  };

  const renderLabel = (icon, text) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem', fontWeight: 700, fontSize: '0.83rem', color: C.textSecondary }}>
      <i className={`fas ${icon}`} style={{ color: C.textMuted, fontSize: '0.8rem' }} />
      {text}
    </label>
  );

  // ============ Render ============
  return (
    <div
      className="pc-withdraw-overlay"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        background: isDark ? '#020617' : '#f1f5f9',
      }}
    >
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1.25rem',
          background: C.overlay,
        }}
      >
        <div
          className="pc-modal-scrollable"
          onClick={(e) => e.stopPropagation()}
          style={{
            width: '100%',
            maxWidth: 'min(560px, calc(100vw - 2rem))',
            padding: '2.25rem 1.75rem',
            borderRadius: '28px',
            background: C.cardBg,
            color: C.textPrimary,
            boxShadow: C.cardShadow,
            border: `1px solid ${C.cardBorder}`,
            position: 'relative',
          }}
        >
          {/* دکمه بستن */}
          <button
            onClick={onClose}
            aria-label="بستن"
            style={{
              position: 'absolute', top: '1.1rem', left: '1.1rem',
              background: C.closeBg, border: 'none',
              width: '36px', height: '36px', borderRadius: '50%',
              cursor: 'pointer', color: C.closeText,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s', fontSize: '0.9rem',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)'; e.currentTarget.style.color = C.danger; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = C.closeBg; e.currentTarget.style.color = C.closeText; }}
          >
            <i className="fas fa-times" />
          </button>

          {/* هدر */}
          <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
            <div style={{
              width: '72px', height: '72px', margin: '0 auto 1rem',
              background: C.iconBg, borderRadius: '22px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: C.iconColor, fontSize: '1.75rem', boxShadow: C.iconShadow,
            }}>
              <i className="fas fa-hand-holding-usd" />
            </div>
            <h3 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800, color: C.textPrimary, letterSpacing: '-0.3px' }}>
              درخواست برداشت وجه
            </h3>
            <div style={{
              marginTop: '0.6rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.4rem 0.9rem', background: C.balanceBg, borderRadius: '999px',
              fontSize: '0.82rem', color: C.textMuted, fontWeight: 600,
            }}>
              <i className="fas fa-wallet" style={{ color: C.balanceText }} />
              موجودی قابل برداشت:&nbsp;
              <strong style={{ color: C.balanceText }}>{maxWithdrawToman.toLocaleString('fa-IR')}</strong>
              &nbsp;تومان
            </div>
          </div>

          {/* نام صاحب کارت */}
          <div style={{ marginBottom: '1.1rem' }}>
            {renderLabel('fa-user', 'نام و نام خانوادگی صاحب کارت')}
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={formData.fullName}
                onChange={(e) => { setFormData((p) => ({ ...p, fullName: e.target.value })); setTouched((p) => ({ ...p, fullName: true })); }}
                placeholder="مثلاً: علی رضایی"
                style={inputStyle('fullName', true)}
              />
              <i className="fas fa-user" style={{
                position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)',
                color: touched.fullName && errors.fullName ? C.danger : C.inputIcon,
                fontSize: '0.9rem', pointerEvents: 'none',
              }} />
            </div>
            {renderError('fullName')}
          </div>

          {/* شماره کارت */}
          <div style={{ marginBottom: '1.1rem' }}>
            {renderLabel('fa-credit-card', 'شماره کارت')}
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={formData.cardNumber}
                onChange={handleCardChange}
                placeholder="6037 9977 1234 5678"
                dir="ltr"
                inputMode="numeric"
                style={{ ...inputStyle('cardNumber', true), fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', letterSpacing: '2px', fontSize: '1.05rem' }}
              />
              <i className="fas fa-credit-card" style={{
                position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)',
                color: touched.cardNumber && errors.cardNumber ? C.danger : C.inputIcon,
                fontSize: '0.9rem', pointerEvents: 'none',
              }} />
            </div>
            {renderError('cardNumber')}
          </div>

          {/* شماره شبا */}
          <div style={{ marginBottom: '1.1rem' }}>
            {renderLabel('fa-university', 'شماره شبا')}
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={formData.shaba}
                onChange={handleShabaChange}
                placeholder="IR000000000000000000000000"
                dir="ltr"
                style={{ ...inputStyle('shaba', true), fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', letterSpacing: '1px', fontSize: '0.88rem' }}
              />
              <i className="fas fa-university" style={{
                position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)',
                color: touched.shaba && errors.shaba ? C.danger : C.inputIcon,
                fontSize: '0.9rem', pointerEvents: 'none',
              }} />
            </div>
            {renderError('shaba')}
          </div>

          {/* مبلغ */}
          <div style={{ marginBottom: '1.25rem' }}>
            {renderLabel('fa-money-bill-wave', 'مبلغ برداشت')}
            <div style={{ position: 'relative' }}>
              <input
                type="number"
                value={formData.rialAmount}
                onChange={(e) => { setFormData((p) => ({ ...p, rialAmount: e.target.value })); setTouched((p) => ({ ...p, rialAmount: true })); }}
                placeholder="مثلاً 1000000"
                style={{ ...inputStyle('rialAmount', true), fontSize: '1.15rem', paddingLeft: '4rem', paddingRight: '1rem' }}
              />
              <span style={{
                position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)',
                color: C.currencyText, fontSize: '0.8rem', fontWeight: 700,
                background: C.currencyBg, padding: '0.3rem 0.65rem', borderRadius: '8px',
                pointerEvents: 'none',
              }}>ریال</span>
            </div>
            {formData.rialAmount && (
              <div style={{
                marginTop: '0.5rem', textAlign: 'center', fontSize: '0.88rem',
                color: isOverLimit ? C.danger : C.primary, fontWeight: 700,
                background: isOverLimit ? C.dangerBg : C.primaryBg,
                padding: '0.55rem', borderRadius: '10px',
                border: `1px solid ${isOverLimit ? C.dangerBorder : 'transparent'}`,
              }}>
                معادل {tomanAmount.toLocaleString('fa-IR')} تومان
                {isOverLimit && (
                  <span style={{ display: 'block', fontSize: '0.75rem', marginTop: '0.25rem', fontWeight: 600 }}>
                    ⚠️ کمبود: {(tomanAmount - maxWithdrawToman).toLocaleString('fa-IR')} تومان
                  </span>
                )}
              </div>
            )}
            {renderError('rialAmount')}
          </div>

          {/* هشدار */}
          <div style={{
            display: 'flex', gap: '0.6rem', alignItems: 'flex-start',
            padding: '0.85rem 1rem', background: C.noticeBg,
            border: `1px solid ${C.noticeBorder}`, borderRadius: '14px', marginBottom: '1.5rem',
          }}>
            <i className="fas fa-info-circle" style={{ color: C.noticeIcon, fontSize: '1rem', marginTop: '2px', flexShrink: 0 }} />
            <span style={{ fontSize: '0.78rem', color: C.noticeText, lineHeight: 1.7, fontWeight: 500 }}>
              مبلغ بلافاصله از کیف پول شما کسر می‌شود و پس از تایید ادمین به حساب شما واریز خواهد شد. اگر درخواست رد شود، مبلغ به کیف پول شما باز می‌گردد.
            </span>
          </div>

          {/* دکمه‌ها */}
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={onClose}
              style={{
                flex: 1, padding: '0.9rem', borderRadius: '14px',
                border: `1.5px solid ${C.outlineBorder}`, background: 'transparent',
                color: C.outlineText, fontWeight: 700, fontSize: '0.9rem',
                cursor: 'pointer', transition: 'all 0.2s', fontFamily: 'inherit',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(241, 245, 249, 0.8)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              انصراف
            </button>
            <button
              onClick={handleSubmit}
              disabled={!isValid}
              style={{
                flex: 2, padding: '0.9rem', borderRadius: '14px', border: 'none',
                background: isValid ? C.primaryBtnBg : C.disabledBg,
                color: isValid ? '#ffffff' : C.disabledText,
                fontWeight: 800, fontSize: '0.9rem',
                cursor: isValid ? 'pointer' : 'not-allowed',
                boxShadow: isValid ? C.primaryBtnShadow : 'none',
                transition: 'all 0.2s', display: 'flex', alignItems: 'center',
                justifyContent: 'center', gap: '0.5rem', fontFamily: 'inherit',
              }}
              onMouseEnter={(e) => { if (isValid) e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <i className="fas fa-paper-plane" />
              ثبت درخواست برداشت
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------
export function PrivateClassProviders({ children }) {
  return (
    <ThemeProvider>
      <ToastProvider>
        <DataProvider>{children}</DataProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}